import type { BuildPipelineSnapshot } from './buildPipelines.js';
/**
 * Creator-platform domain types — the Firestore document shapes from
 * docs/CREATOR-PLATFORM.md "Data model", transcribed VERBATIM (collections,
 * fields, statuses). These are the typed contracts every repo in this package
 * reads and writes; the portal, builder, and functions consume them too.
 *
 * Timestamps: fields written with the server clock are typed `number` here
 * (millis since epoch, as the doc store resolves the {@link
 * import('@djgg/core').ServerTimestampSentinel}); on write the repos pass the
 * sentinel, on read a resolved number comes back.
 */
import type { BuildProgress } from './buildProgress.js';
import type { AudioGenerationFlags, AudioGeneratorProvenanceV1, MusicBriefV1, SfxBriefV1 } from './audioGeneration.js';
import type { AvoidDirection } from './avoidDirection.js';
import type { ComposedFailureKindV1 } from './composedFailure.js';
import type { Uid } from '@djgg/core';
import type { ControlSchemeId, SchemeParams } from '@djgg/controls';
import type { GameSettings, PartialGameSettings } from './spec/types.js';
import type {
  GameDesign,
  ReferenceBrief,
  SpriteOrientationContract,
  VideoBrief,
} from './design/types.js';
import type { CreationRating } from './rating/evaluate.js';
import type { CompositionDeferredMediaV1 } from './spec/gameSpec.js';
import type { RuntimeContractRefV1 } from './runtimeRef.js';
import type { GameplayCapabilityLockV1 } from '@djgg/gameplay-kit';
import type { AcceptedQualityRunV1 } from './gameQualityContract.js';
import type { CompositionConversionV1 } from './compositionConversion.js';
import type { AcceptedBuildExecutionPolicyV1 } from './buildExecutionPolicy.js';
import type { CandidateInventoryEntryV1, ImmutableCandidateObjectV1 } from './failedCandidate.js';
import type {
  AcceptedCompositionV2,
  CompositionPredecessorContextV2,
  CompositionResolutionV2,
  PublicationProofV2,
} from './spec/compositionV2.js';
import type { ComponentRegistryStateV2 } from './componentRegistryStateV2.js';
import type { ComposePlanV1 } from './composePlan.js';
import type { ComposedStepV1, ComposedSynthesisResultV1 } from './spec/composedSelection.js';

/** A creation's game style/category (gallery filter + badge). */
export type CreationStyle = 'rpg' | 'action' | 'shooter' | 'sidescroller' | 'puzzle' | 'strategy' | 'word';

/**
 * Species discriminator for a `creations/{id}` doc. Absent means `'creation'`
 * (every doc written before the field existed IS an LLM creation — readers must
 * treat `undefined` as `'creation'`). `'native'` marks a first-class djgg game
 * (pong, tetris, …) seeded into the collection so the FULL social machinery
 * (likes, view/play counters, credits, updateCreation) applies one-path style —
 * but natives have NO builder lifecycle: requestBuild / setActiveVersion /
 * deleteVersion / deleteCreation reject them (see creatorHandlers), and the
 * game page iframes the native manifest path, never a bundle URL. Server-owned
 * (in `creationServerFields`): only the trusted seed path
 * ({@link CreationsRepo.ensureNative}) ever writes it.
 */
export type CreationKind = 'creation' | 'native';

/** Starter template family a creation seeds from. `'native'` is stamped ONLY on
 *  `kind: 'native'` docs (platform games have no starter template); it is never
 *  accepted by createCreation and never reaches the builder (builds on natives
 *  are rejected). */
export type TemplateFamily =
  | 'paddle'
  | 'shooter'
  | 'gallery-shooter'
  | 'puzzle'
  | 'blocks'
  | 'sidescroller'
  // RETIRED (2026-09-03): stored docs only — see RETIRED_TEMPLATE_FAMILIES.
  | 'platformer'
  | 'wordplay'
  | 'service'
  | 'duel'
  | 'neutral'
  | 'native';

/** Version pins for the durable concept-analysis operation. These are stored
 * with the canonical input rather than inferred by readers, so a later prompt,
 * profile catalog, or fingerprint algorithm cannot reinterpret an old draft. */
export const CONCEPT_ANALYZER_CONTRACT_VERSION = 2 as const;
export const CONCEPT_ANALYSIS_FINGERPRINT_VERSION = 1 as const;
/** Long enough for one strong-model proposal plus its bounded repair. The
 * callable and outbound request retain a 30-second shutdown margin. */
export const CONCEPT_ANALYSIS_LEASE_MS = 9 * 60 * 1000;
export const CONCEPT_ANALYSIS_MAX_QUESTIONS = 3;
export const CONCEPT_ANALYSIS_MAX_CLARIFICATION_CYCLES = 2;
/** How long a fresh Create waits for its creator to answer the film's
 * questions before it carries on without them. */
export const CONCEPT_QUESTIONS_WAIT_MS = 2 * 60_000;
/** What Deej answers for a creator who leaves a question alone: the first
 * offered option, or, for a question with no options, a free hand. */
export const UNANSWERED_TEXT_ANSWER = 'No preference: pick what fits the idea best.';
export const unansweredConceptAnswer = (question: Pick<ConceptClarificationQuestion, 'options'>): string =>
  question.options[0]?.id ?? UNANSWERED_TEXT_ANSWER;

export type ConceptAnalysisPlayMode =
  | 'solo'
  | 'vsBot'
  | 'localVersus'
  | 'onlineVersus';

/** The complete proposal shape that Studio reviews. `design.platformer`, when
 * present, contains the keyed level plans and resolved capability snapshot. */
export interface ConceptAnalysisProposal {
  design: GameDesign;
  engineHint: Exclude<TemplateFamily, 'native'>;
  style: CreationStyle;
  playModes: ConceptAnalysisPlayMode[];
  touchScheme: ControlSchemeId;
  touchParams: SchemeParams;
  orientation: GameSettings['orientation'];
  orientationReason: string;
}

export type ConceptAnalysisDecisionBasis =
  | 'explicit'
  | 'inferred'
  | 'defaulted'
  | 'answered'
  | 'requires-answer';

export interface ConceptAnalysisDecisionProvenance {
  /** Stable design/capability field path, such as `movement` or `profile`. */
  field: string;
  basis: ConceptAnalysisDecisionBasis;
  reason: string;
}

export interface ConceptAnalysisAssumption {
  field: string;
  reason: string;
}

export interface ConceptAnalysisUnsupportedIntent {
  intent: string;
  nearestSupportedCapability: string;
  reason: string;
}

export interface ConceptAnalysisMetadata {
  decisions: ConceptAnalysisDecisionProvenance[];
  assumptions: ConceptAnalysisAssumption[];
  unsupportedIntents: ConceptAnalysisUnsupportedIntent[];
  /** True only for the conservative, visible platformer recovery proposal. */
  fallbackUsed: boolean;
}

export interface ConceptClarificationOption {
  id: string;
  label: string;
}

export interface ConceptClarificationQuestion {
  id: string;
  field: 'movement' | 'campaign' | 'combat' | 'damageRetry' | 'progression';
  prompt: string;
  /** Choice is the legacy/default shape. Text lets the creator supply missing
   *  authority without forcing their idea into analyzer-authored options. */
  answerMode?: 'choice' | 'text';
  options: ConceptClarificationOption[];
}

/** Version-1 persisted result. The draft alias remains readable for resume. */
export type ConceptAnalysisV1Result =
  | {
      kind: 'proposal';
      proposal: ConceptAnalysisProposal;
      metadata: ConceptAnalysisMetadata;
    }
  | {
      kind: 'clarification';
      questions: ConceptClarificationQuestion[];
      draftProposal: ConceptAnalysisProposal;
      metadata: ConceptAnalysisMetadata;
    };

export interface ConceptAnalysisValidationFailure {
  contractVersion: 2;
  kind: 'failure';
  retryable: boolean;
  /** Bounded parser evidence from the model's failed attempts. */
  validationErrors: string[];
}

/** Version-2 result. Only a proposal carries buildable design authority. */
export type ConceptAnalysisV2Result =
  | {
      contractVersion: 2;
      kind: 'proposal';
      proposal: ConceptAnalysisProposal;
      metadata: ConceptAnalysisMetadata;
    }
  | {
      contractVersion: 2;
      kind: 'clarification';
      questions: ConceptClarificationQuestion[];
      metadata: ConceptAnalysisMetadata;
    }
  | ConceptAnalysisValidationFailure;

export type ConceptAnalysisResult = ConceptAnalysisV1Result | ConceptAnalysisV2Result;

export interface ConceptAnalysisCanonicalInputs {
  /** Explicit saved creator choices captured before analysis. Absent on legacy operations. */
  creatorSettings?: PartialGameSettings;
  settingsRevision?: number;
  title: string;
  concept: string;
  /** Normalized, de-duplicated, lexically ordered URLs. */
  referenceUrls: string[];
  fingerprint: string;
  analyzerContractVersion: number;
  profileCatalogVersion: number;
  fingerprintAlgorithmVersion: number;
}

export interface ConceptAnalysisEvidenceDigest {
  kind: 'reference' | 'video';
  source: string;
  digest: string;
}

export interface StaleConceptAnalysisOperation {
  operationId: string;
  fingerprint: string;
  status: 'stale';
  staleAt: number;
}

export type ConceptAnalysisStatus =
  | 'claimed'
  | 'clarification'
  | 'proposal'
  | 'failed'
  | 'stale'
  | 'consumed';

export interface ConceptAnalysisFailure {
  code: 'quota' | 'unavailable' | 'validation' | 'internal';
  message: string;
  retryable: boolean;
  failedAt: number;
}

export type ConceptAnalysisProgressStage =
  | 'analyzing'
  | 'clarification'
  | 'ready'
  | 'failed'
  | 'stale';

/** Safe, bounded creator-facing evidence. It deliberately cannot carry raw
 * prompts, provider replies, source, or model reasoning. */
export interface ConceptAnalysisArtifactSummary {
  id: string;
  kind: 'reference' | 'video' | 'gameplay-idea' | 'vocabulary';
  state: 'ready' | 'unavailable';
  title: string;
  summary: string;
  /** Public preview only; raw captures never enter this projection. */
  thumbnailUrl?: string;
}

export interface ConceptAnalysisProgress {
  stage: ConceptAnalysisProgressStage;
  updatedAt: number;
  completedArtifacts: ConceptAnalysisArtifactSummary[];
}

/** One stage of the composed stage machine: the plan, each planned step in
 * order, then the proven synthesis. */
export type ComposedRunStageV1 = 'plan' | `step-${number}` | 'synthesis';

/** The durable stage machine every server-owned composed run checkpoints:
 * plan → the plan's steps (receipted) → synthesis (proof receipted). A claim
 * is identified by `(claimantId, attempt, stage)`; late work from a reaped
 * lease cannot complete after any of those values changes. Each paid result
 * is saved here before the next stage begins, so a refresh resumes instead of
 * paying for completed work again. */
export interface ComposedRunStagesV1 {
  /** Optional caller-lowered ceiling for one exact claimed provider stage.
   * Once used, every later paid claim must supply a new explicit ceiling. */
  providerBudget?: { stage: string; claimantId: string; maxProviderCostMicros: number };
  /** Independent reviewed checks survive component regeneration and synthesis. */
  ruleReviews?: import('./composedRuleReview.js').ComposedRuleReviewV1[];
  ruleReviewEpoch?: number;
  claimantId: string | null;
  leaseExpiresAt: number | null;
  attempt: number;
  /** Server-owned durable count of explicit paid-stage reclaims. Each genuine
   * reclaim raises this run's cumulative generation cap by the fixed retry
   * allowance; absent on runs created before the allowance was introduced. */
  approvedPaidRetryCount?: number;
  plan?: ComposePlanV1;
  steps: ComposedStepV1[];
  result?: ComposedSynthesisResultV1;
  failure?: {
    message: string;
    /** Absent on a run that failed before kinds were named. */
    kind?: ComposedFailureKindV1;
    retryable: boolean;
    failedAt: number;
  };
  /** Automatic retries of this stage after a provider or builder outage, or a
   * lost stage. Cleared when the stage finishes. No stage starts before `notBefore`.
   * `since` is the first retry's time: the outage's spend counts from it. */
  temporaryRetry?: { count: number; notBefore: number; message: string; since: number };
  /** A bounded, server-owned recovery of a generated rule. The failed
   * provider stage is retried at most twice from this checkpoint, with its
   * prior admission failures supplied to the next attempt. */
  recovery?: {
    stage: ComposedRunStageV1;
    message: string;
    automaticRetries: 1 | 2;
    startedAt: number;
  };
  createdAt: number;
  updatedAt: number;
}

/** Durable, member-readable checkpoint for the paid composed Create chain
 * of a fresh creation (in its private analysis draft). The browser may start
 * or observe one stage. */
export interface ComposedCreateRunV1 extends ComposedRunStagesV1 {
  /** Set when a fresh Create's idea couldn't be finished and the run started over
   * on the closest stock game. Saves why, for admins. Happens at most once. */
  fallback?: { kind: ComposedFailureKindV1; stage: string; message: string; at: number };
  /** Unpaid build acceptance failed; the proven result remains reusable. */
  finalizationFailure?: { message: string; at: number };
  schemaVersion: 1;
  sourceOperationId: string;
  sourceRevision: number;
  input: {
    title: string;
    concept: string;
    design: GameDesign;
  };
  status: 'ready' | 'claimed' | 'prepared' | 'failed';
  /** The stage currently claimed, or the next stage after a failure. */
  stage: ComposedRunStageV1;
}

/** The live version a composed revision changes, fixed when the run starts
 * and compare-and-swapped again at build acceptance. `authorityDigest` is the
 * version's accepted composition digest (a composed@1 game: the run's first
 * step is its receipted `base`), or null for a live GENERIC game converted
 * onto the composed builder (no base step; the plan starts from the catalog). */
export interface ComposedRevisionBaseRefV1 {
  version: number;
  authorityDigest: string | null;
}

/** A server-owned composed REVISION of a live creation
 * (`creations.composed_revision_run`, server-private): the composed stage
 * machine with the prior game entering as its receipted `base` step, then an
 * automatic `build` stage that writes the revised design/settings through the
 * ordinary writers and accepts one ordinary build of the SAME creation
 * (requestBuild with `commandId === runId`). There is no confirm step. */
export interface ComposedRevisionRunV1 extends ComposedRunStagesV1 {
  schemaVersion: 1;
  /** Stable run identity; the build command id of its `build` stage. */
  runId: string;
  base: ComposedRevisionBaseRefV1;
  input: {
    title: string;
    /** The creation's own concept (the game the revision keeps). */
    concept: string;
    /** What the creator asked to change; null for a conversion of a generic
     * game onto the composed builder, which changes nothing on purpose. */
    request: string | null;
    /** Frozen reviewed conversion contract, bound to the immutable source. */
    conversion?: import('./composedConversion.js').ComposedConversionV1;
    /** The base version's design snapshot. */
    design: GameDesign;
  };
  /** Collaboration attribution fixed at the run's start and used by its
   * build: `updatedBy` is the member it is attributed to, and
   * `actionPerformedBy` who actually started it. */
  actor: { updatedBy: Uid; actionPerformedBy: Uid };
  /** `accepted` is terminal: the build stage accepted its build. */
  status: 'ready' | 'claimed' | 'accepted' | 'failed';
  stage: ComposedRunStageV1 | 'build';
  /** Present once the build stage accepted the build. */
  build?: { version: number; jobId: string; acceptedAt: number };
  /** Why the run stopped: a stage failed (`stage-failed`; resumable at that
   * stage), the live game changed under it (`base-changed`; terminal), or its
   * synthesis left the game exactly as it is (`no-change`; terminal, nothing built). */
  failure?: ComposedRunStagesV1['failure'] & { code: 'stage-failed' | 'base-changed' | 'no-change' };
  /** What the creator is shown, saved with the run so a reload keeps it:
   * `changes` once synthesis is saved — the "what changed" lines; `art` once
   * the build is accepted — the slots whose art is kept (and why) and the
   * slots drawn again. */
  summary?: {
    changes: string[];
    art?: { kept: Array<{ slot: string; reason: 'same-basis' | 'creator-attached' }>; redrawn: string[] };
  };
}

/** Server-owned compare-and-swap state stored on the creation. A claim is
 * identified by `(operationId, claimantId, attempt)`; late work from a reaped
 * lease cannot complete after any of those values changes. */
export interface ConceptAnalysisDraft {
  operationId: string;
  revision: number;
  status: ConceptAnalysisStatus;
  inputs: ConceptAnalysisCanonicalInputs;
  claimantId: string | null;
  leaseExpiresAt: number | null;
  attempt: number;
  questionCycle: number;
  partialAnswers: Record<string, string>;
  /** Present on a claim whose answers Deej gave because the creator left the
   * questions alone. While it is claimed, the creator's own answers to these
   * questions still replace Deej's. */
  answeredForCreator?: { questions: ConceptClarificationQuestion[] };
  staleOperations: StaleConceptAnalysisOperation[];
  createdAt: number;
  updatedAt: number;
  /** Durable member-readable journey projection. Absence denotes a legacy
   * operation; clients must not infer completed artifacts from it. */
  progress?: ConceptAnalysisProgress;
  /** Paid composed planning/proof checkpoints. Kept in the server-private
   * analysis draft and exposed only through the guarded resume callable. */
  composedRun?: ComposedCreateRunV1;
  quotaReservedAt?: number | null;
  /** The quota counter and this marker commit in one transaction. Recovery
   *  skips charging only when this field is present. */
  quotaConsumedAt?: number | null;
  result?: ConceptAnalysisResult;
  referenceBriefs?: ReferenceBrief[];
  videoBriefs?: VideoBrief[];
  evidenceDigests?: ConceptAnalysisEvidenceDigest[];
  failure?: ConceptAnalysisFailure;
  consumedVersion?: number;
}

/** At-most-once sidecar for advisory named-game video enrichment. It is kept
 * outside `analysisDraft` so claiming it cannot invalidate the revision that
 * the Create journey is already composing. */
export interface VideoReferenceEnrichmentState {
  operationId: string;
  status: 'claimed' | 'complete' | 'failed';
  claimantId: string;
  startedAt: number;
  completedAt?: number;
  failure?: { code: 'unavailable' | 'internal' | 'uncertain'; message: string };
}

/** Neutral, incomplete manual editing state; never build authority by itself. */
export interface ConceptManualDesignDraft {
  contractVersion: 1;
  revision: number;
  sourceRevision: number;
  title: string;
  concept: string;
  design: Partial<GameDesign>;
  unresolvedFields: string[];
  updatedAt: number;
}

/**
 * Lifecycle status of a creation (the published game record). PURE lifecycle:
 * `live` means an active version is playable, `failed` means NOTHING is
 * playable (first build never succeeded). Build PROGRESS is NOT lifecycle —
 * it rides {@link Creation.buildState}, so an iterating game with a playable
 * active version stays `live` (and in the gallery) for the whole build.
 * `'building'` remains in the union only for pre-decoupling docs; no writer
 * sets it anymore (readers treat it as live-if-playable).
 */
export type CreationStatus = 'draft' | 'building' | 'live' | 'failed';

/** An unbuilt draft: a wizard run that never accepted a build. It is what the
 *  per-creator draft cap counts, the one kind of creation its creator may delete,
 *  and in the Studio a run to resume rather than a game to open. */
export function isUnbuiltDraft(c: { status: CreationStatus; latestVersion?: number }): boolean {
  return c.status === 'draft' && (c.latestVersion ?? 0) === 0;
}

/** Build PROGRESS on a creation — orthogonal to {@link CreationStatus}.
 *  Absent means `'idle'` (docs predate the field). Server-owned: requestBuild
 *  sets 'building'; the builder outcome / reaper / cancel return it to 'idle'. */
export type BuildState = 'idle' | 'building';

/** Gallery visibility of a creation. `'draft'` is the PLATFORM-MANAGED fork
 *  state (readable only by members + superadmin, enforced by RLS): a fork is
 *  born `draft` and only the builder's divergence finalize ever exits it (to
 *  `unlisted`, like every birth) — a DB trigger rejects any client transition
 *  in or out (0010_forking.sql). Creators choose between `public` and
 *  `unlisted` only: everything is BORN `unlisted` (link-shareable, playable,
 *  not listed), and "publish" is the member-permitted `unlisted→public` flip
 *  through updateCreation (the publish gate, 0051). */
export type Visibility = 'public' | 'unlisted' | 'draft';

/** LLM engine selection stamped on a creation (creator-picked). */
export interface EngineRef {
  provider: string;
  model: string;
}

/** One collaborating editor on a creation (see {@link Creation.editors}). */
export interface EditorEntry {
  uid: Uid;
  /** Denormalized display handle (may go stale on handle change, like
   *  `creatorHandle`; the uid is authoritative). */
  handle: string;
}

/**
 * Display snapshot of the parent a fork seeded from (see
 * {@link Creation.forkedFrom}) — everything the lineage credit line renders,
 * captured at fork time so it needs no lookups and survives parent deletion
 * or renames. Credit-via-lineage: the parent's creator AND editors are named.
 */
export interface ForkLineage {
  slug: string;
  title: string;
  creator: { uid: Uid; handle: string };
  /** The parent's editor roster at fork time (credited, never carried over
   *  as members — a fork starts with the forker alone; origin R4). */
  editors: EditorEntry[];
  /** The parent version the fork's v1 was seeded from (its activeVersion at
   *  fork time). */
  seedVersion: number;
}

/**
 * Per-game Editors' Pick marks — curated by the weekly LLM reviewer + the
 * superadmin override (see the pickEditorsPicks function). Two honors ride
 * here:
 *  - `everSelected` drives the PERMANENT, film-festival "Official Selection"
 *    laurel on the gallery card + game page. Once true it never returns to
 *    false — a game keeps the laurel after it rotates out of the active list.
 *  - `current` marks membership in the ACTIVE weekly list: the gallery
 *    Editors' Pick row is derived from `current === true` (ordered by
 *    `currentRank`), and the card/page show a live "Current Selection"
 *    emphasis on top of the laurel.
 * Server-owned: written ONLY by the trusted picker (CreationsRepo.setEditorsPick);
 * no client column grant exists. Absent on every creation never selected.
 */
export interface CreationEditorsPick {
  /** Selected at least once — the permanent laurel. Never flips back. */
  everSelected: boolean;
  /** Millis of the FIRST selection (the laurel's "since"). */
  firstSelectedAt: number;
  /** Millis of the most recent selection. */
  lastSelectedAt: number;
  /** How many editions this game has been selected in — the film-festival
   *  "N-time Official Selection" count. */
  selectionCount: number;
  /** Edition labels it was selected in, oldest first (e.g. `['2026-W28']`). */
  editions: string[];
  /** In the ACTIVE weekly list right now (rotates; the laurel stays). */
  current: boolean;
  /** 1-based order within the active list (the gallery row's sort key).
   *  Absent when not `current`. */
  currentRank?: number;
  /** The creation's activeVersion whose cached LLM review backed the most
   *  recent selection — stamped at selection time so the shelf/laurel and the
   *  Admin panel can show WHICH build was reviewed even after newer versions
   *  ship between runs (0 for natives; absent on pre-stamp marks). */
  reviewedVersion?: number;
  /** The cached review score (0..100) that backed that selection. */
  reviewedScore?: number;
}

/**
 * `creations/{creationId}` — the published game record. `slug` is unique
 * platform-wide (claimed via `slugs/{slug}`); counters (`likesCount`,
 * `viewsCount`) are server-maintained rollups, never authored by the creator.
 */
export interface Creation {
  /** Permanent marker preventing a legacy creation from entering the
   * conversion admission path twice under different commands. */
  compositionConversion?: {
    commandId: string;
    version: number;
    authorityDigest: string;
  };
  /** The creation's current composed revision run (server-private column
   * `composed_revision_run`; read only through its guarded callable). */
  composedRevisionRun?: ComposedRevisionRunV1 | null;
  /** Server-owned revision fences attachment against concurrent rejection. */
  assetPolicyRevision?: number;
  /** Species discriminator — see {@link CreationKind}. ABSENT on every doc
   *  that predates the field and on all normal LLM creations; only seeded
   *  native-game docs carry `'native'`. Read as `kind ?? 'creation'`. */
  kind?: CreationKind;
  slug: string;
  title: string;
  /** Display-title provenance. Historical rows were backfilled as creator;
   * automatic selection may replace only the same run's temporary authority. */
  titleAuthority?: import('./creationArtifacts.js').CreationTitleAuthorityV1;
  /** Stable authority for the original Create receipt and reusable artifacts. */
  creationRunId?: string | null;
  description: string;
  style: CreationStyle;
  creatorUid: Uid;
  creatorHandle: string;
  /** Collaborating EDITORS (never includes the creator). Editors hold the
   *  creator's powers except deleting versions/the creation and managing this
   *  roster. Handles are denormalized for display (same precedent as
   *  `creatorHandle`); add/remove history lives in the action log (Firestore
   *  forbids serverTimestamp inside arrays). Server-owned — mutated only via
   *  the trusted addEditor/removeEditor functions. */
  editors: EditorEntry[];
  /** Flat membership index: `[creatorUid, ...editor uids]`. Enables the single
   *  `array-contains` query behind "games I can edit" (Firestore cannot OR a
   *  creatorUid equality with an editors lookup) and the rules' member check.
   *  Server-owned; ALWAYS recomputed with `editors` in the same write. */
  memberUids: Uid[];
  /** Starter family the creation seeded from. */
  template: TemplateFamily;
  /** Creator-selected LLM engine. */
  engine: EngineRef;
  status: CreationStatus;
  /** Highest published version number (0 until the first successful build). */
  latestVersion: number;
  /** Public GCS URL of the latest live bundle (empty until first live build). */
  latestBundleUrl: string;
  /** The version currently PLAYED (active/served). Defaults to and follows
   *  `latestVersion`; a rollback pins it to an older live version via the
   *  trusted `setActiveVersion`. 0 until the first live build. */
  activeVersion: number;
  /** Public GCS URL of the active version's bundle — what the game page iframes.
   *  Empty until the first live build; readers fall back to `latestBundleUrl`. */
  activeBundleUrl: string;
  /** Server-maintained like count (mirrored by a function from the likes subcollection). */
  likesCount: number;
  /** Server-maintained comment count for THIS game (not per version) — mirrored
   *  by the post/delete callables from the comments subcollection, exactly like
   *  {@link likesCount}. Absent on docs seeded before comments shipped (read as 0). */
  commentsCount?: number;
  /** Server-maintained view rollup (sum of viewShards, rolled up by a scheduled function). */
  viewsCount: number;
  /** Distinct signed-in viewers (incremented once per uid via the `viewers/{uid}`
   *  marker). Server-maintained. */
  distinctViewers: number;
  /** Engaged plays — a play is counted when a viewer stays past a dwell threshold
   *  (stronger signal than a raw view). Server-maintained. */
  playsCount: number;
  /** When a play last BEGAN on this game (epoch ms; 0 = never played) — the
   *  gallery's "Last played" sort key. Server-owned, written by the two funnels
   *  that mean a play began: the engaged-play bump ({@link ViewsRepo.incrementPlays},
   *  which counts anonymous portal plays) and the new-session edge of a presence
   *  beat ({@link CreationsRepo.stampLastPlayed}, which catches direct and
   *  installed-PWA launches). Both write a server clock, so it advances on its
   *  own. NOT bumped by `updatedAt`-style content edits — playing a game must
   *  not move it in trending or under "Latest", and vice versa. */
  lastPlayedAt: number;
  visibility: Visibility;
  /** Millis of the creation's FIRST `unlisted→public` flip — the set-once
   *  publish stamp. NOT the listing truth ({@link visibility} is): it exists
   *  for debut-fair "first published" ordering and as the count-once key for
   *  the parent {@link forksCount} increment on a fork's first publish.
   *  Server-owned, written ONLY by {@link CreationsRepo.publishFlip} (set-once
   *  inside the flip's transaction — an unpublish→republish never re-stamps);
   *  rows public (or diverged) before the gate were grandfather-stamped by
   *  0051. Absent/null = never published. */
  publishedAt?: number | null;
  /** The game's QUALITY RATING — playability (the rendered playtest audit on the
   *  active version) fused with appeal (the Editors' Pick rubric score). See
   *  `rating/evaluate.ts` for why neither signal ranks the catalogue alone.
   *  Server-owned, written ONLY by {@link CreationsRepo.setRating}. Absent =
   *  never rated (a game born after the last rating pass); consumers treat that
   *  as unknown, never as bad. */
  rating?: CreationRating;
  /** Build progress (absent = idle). See {@link BuildState}; decoupled from
   *  `status` so an iterating game never leaves the gallery. Server-owned. */
  buildState?: BuildState;
  /** Paid music jobs accepted for this creation. Missing on historical rows is
   * zero. Server-owned and incremented with the accepting transaction. */
  musicGenerationCount?: number;
  /** The creator's ANSWERED wizard decisions (sparse — derived defaults are
   *  recomputed at build time and never stored as answers; see spec/).
   *  Server-owned: written ONLY via createCreation/updateGameSettings
   *  (CreationsRepo.setSettings is the single writer). Absent on docs that
   *  predate the wizard until the settings backfill runs. */
  settings?: PartialGameSettings;
  /** Schema version the stored {@link settings} were written under (see
   *  spec/migrate.ts — read lazily, migrated on the next write). */
  settingsSchemaVersion?: number;
  /** Monotonic settings revision, bumped on every settings write. Compared
   *  against the version doc's `settingsRev` stamp to drive the "pending
   *  settings changes" banner. Server-owned. */
  settingsRev?: number;
  /** Protected DB-owned final-source epoch, incremented on every reviewed
   * source mutation including metadata/retained context. Never client-written. */
  compositionIntentSourceRev?: number;
  /** The creator's VERBATIM free-text concept from the wizard's concept step
   *  (provenance; the design doc below is derived from it). '' on creations
   *  that predate concept-first creation. */
  concept?: string;
  /** Server-only identity of the accepted initial draft request. Never edited
   * with the working concept/settings; retries may arrive after those change. */
  createRequestFingerprint?: string;
  /** Durable, server-owned proposal/clarification operation. It remains
   * pending authority until Build consumes it; reloads and retries reuse this
   * exact state instead of silently re-running analysis. */
  analysisDraft?: ConceptAnalysisDraft;
  /** Separately persisted neutral manual work. It may be incomplete and is
   * never consumed as build authority until canonical validation promotes it. */
  conceptManualDraft?: ConceptManualDesignDraft;
  /** The creation's MECHANICS AUTHORITY (see design/types.ts) — derived from
   *  the concept by the analysis pass, creator-editable, re-sent on every
   *  build. Server-owned: written ONLY via updateGameDesign
   *  (CreationsRepo.setDesign) and the builder's iterate auto-amend. Absent
   *  on creations that predate concept-first creation. */
  gameDesign?: GameDesign;
  /** Monotonic design revision, bumped on every design write — the design
   *  analog of {@link settingsRev} (drives the pending-changes banner vs the
   *  version doc's designRev stamp). Server-owned. */
  designRev?: number;
  /** Fetched-and-distilled reference pages from concept time (≤3; see
   *  design/types.ts ReferenceBrief). Server-owned, written once by the
   *  concept-analysis pass; re-sent to every build without refetching. */
  referenceBriefs?: ReferenceBrief[];
  /** Watch reports for games the creator's concept REFERENCED but did not link
   *  (≤2; see design/types.ts VideoBrief). Server-owned, written by the
   *  concept-analysis pass and APPENDED by an iteration that names a new
   *  reference; re-sent to every build without re-watching. */
  videoBriefs?: VideoBrief[];
  /** Durable at-most-once state for the non-blocking video-reference task. */
  videoReferenceEnrichment?: VideoReferenceEnrichmentState;
  /** Optional 16:9 cover image URL shown on the gallery card. Absent for most
   *  creations (the card renders a deterministic gradient); may be set for
   *  showcase/seeded games or a future asset-pipeline cover. */
  coverUrl?: string;
  /** Transparent, game-matched title treatment generated for the media story.
   *  Consumers MUST only use it when titleOverlayForTitle exactly matches the
   *  current title; the pair is committed atomically by the worker. */
  titleOverlayUrl?: string;
  titleOverlayForTitle?: string;
  /** Follow-active passive gameplay preview (migration 0036). */
  previewUrl?: string | null;
  /** Optional static poster retained for creations that already published one. */
  posterUrl?: string | null;
  /** Asset-studio assets ATTACHED to this creation (an approved pick from the
   *  generated variants). Server-owned — mutated only via the trusted
   *  attachAsset function; absent on creations that never used the studio. */
  assets?: CreationAsset[];
  /** Trusted working-copy audio selection. Publication snapshots it, never reads it live. */
  /** The Studio sound selection the next GENERIC build publishes. Null once
   * a composed build of the game went live: the composed soundtrack is the
   * media child's, so a generic-era draft is retired there, audited. */
  sfxDraft?: { spec: import('@djgg/core').CuratedSfxSpec; actorUid: string; updatedAtMs: number } | null;
  /** The creator's saved "avoid this visual/audio direction" preference
   *  (migration 0078). Written ONLY by `updateCreation`; read by the Studio
   *  image prompt and folded into the audio brief at kickoff. Steering only —
   *  exact-byte rejection is the block, and this record is never consulted by
   *  it. Absent until a member sets one. */
  avoidDirection?: AvoidDirection;
  /** A cheap-LLM-drafted EXAMPLE "next change" prompt in the game's own
   *  language, prefilled into the Iterate box as an editable starting point.
   *  Regenerated once per build (create + each iterate) from the design + latest
   *  build; backfilled once for pre-existing creations. Absent until first
   *  generated. Server-owned. */
  suggestedIteratePrompt?: string;
  /** A cheap-LLM-drafted EXAMPLE asset-generation prompt (a new sprite/asset
   *  that would suit this game), prefilled into the Asset studio prompt box.
   *  Same lifecycle as {@link suggestedIteratePrompt}. Server-owned. */
  suggestedAssetPrompt?: string;
  // ── forking lineage (0010_forking.sql; absent on everything pre-fork) ──────
  // All server-owned by construction: written only by CreationsRepo.createFork
  // and the builder's divergence finalize — no client column grants exist.
  /** Id of the creation this one was FORKED from; absent on non-forks. Kept as
   *  a live pointer for the forks-list query; nulled by the DB if the parent
   *  is deleted (display always renders from {@link forkedFrom} instead). */
  forkParentId?: string | null;
  /** LINEAGE ROOT of the fork chain (a fork of a fork keeps the original's
   *  root — chain propagation). Absent on non-forks. Queryable reporting:
   *  "everything descended from X" (origin R3). */
  rootCreatorUid?: Uid;
  rootCreatorHandle?: string;
  /** Parent display snapshot for the credit line — see {@link ForkLineage}. */
  forkedFrom?: ForkLineage;
  /** Millis of the fork's FIRST divergence (the build after its seeded v1 went
   *  live), stamped once by the builder when it flips the fork out of `draft`
   *  (to `unlisted` — the publish gate; the owner publishes from the studio
   *  like any other creation). Absent until then; the forks-list sort key. */
  divergedAt?: number | null;
  /** How many forks of THIS creation have PUBLISHED (count-once: incremented
   *  exactly when a fork's first publish wins the {@link publishedAt} set-once
   *  CAS — never on fork creation or divergence). Forks counted at divergence
   *  under the pre-gate rules arrive grandfather-stamped by 0051, so they can
   *  never re-count. Absent on docs that predate forking (read as 0). */
  forksCount?: number;
  /** Editors' Pick marks — the permanent "Official Selection" laurel plus
   *  active-list membership. Server-owned (pickEditorsPicks); absent until the
   *  game is first selected. See {@link CreationEditorsPick}. */
  editorsPick?: CreationEditorsPick;
  createdAt: number;
  updatedAt: number;
}

/** Asset kinds. `background` renders through the board pipeline (sized);
 *  `cover` is 16:9 opaque key art (board pipeline too, no chroma matte) that
 *  drives the gallery card + in-game menu splash and is NEVER a manifest-bound
 *  drawable; `strip` is a PLATFORM-GENERATED horizontal animation frame strip
 *  (never creator-requested or uploaded — the strip pipeline derives it from
 *  an entity's base sprite; see {@link StripAssetMeta}); the rest are
 *  sprite-pipeline cutouts a creator can generate. */
/** Music/SFX are inspected PCM WAV media, never drawable textures or inputs to
 * the image-generation pipeline. Both carry trusted `audio` metadata. */
export type CreationAssetKind = 'sprite' | 'background' | 'icon' | 'palette' | 'cover' | 'strip' | 'music' | 'sfx';

/** How a Studio cover generation gets its visual direction. Game assets are
 * the default: use the creation's attached playable art as image references.
 * Concept keeps the original prompt-only cover workflow for creators who want
 * a fresh interpretation before their game has art, or simply prefer it. */
export type CoverGenerationSource = 'game-assets' | 'concept';

/**
 * Structured metadata on a `strip`-kind asset/variant — the per-state cell
 * geometry plus the identity pair the binding injector keys on (exact
 * `entityName`/`state`, never token-matched). `referenceVariantId` is the base
 * sprite variant the strip was generated FROM, persisted so a base-sprite
 * regen can identify stale strips deterministically. The asset's `name` is
 * ALWAYS the derived `<entity>-<state>` key (see `stripAssetName` /
 * `assertStripAssetName` in animation.ts) — a structural invariant, never a
 * creator-editable label.
 */
export interface StripAssetMeta {
  entityName: string;
  /** A member of the closed ANIM_STATE_NAMES vocabulary. */
  state: string;
  /** Cell count; the server-side re-pack guarantees
   *  `image width === frames * frameWidth`. */
  frames: number;
  frameWidth: number;
  frameHeight: number;
  /** The base-sprite variant used as the generation reference. */
  referenceVariantId: string;
}

/**
 * Which path put an attached asset in place (per-asset provenance, stamped at
 * attach time by every writer):
 *  - `'build'`  — build-time plan generation (the builder's finalize attach);
 *  - `'bank'`   — the default sprite bank filled a generation miss;
 *  - `'studio'` — the asset studio's generate→attach flow;
 *  - `'upload'` — a creator-uploaded PNG (bring-your-own-art).
 * An asset with NO source predates provenance and is treated as CREATOR-OWNED
 * everywhere the distinction matters (see {@link isBuildReplaceable}).
 */
export type CreationAssetSource = 'build' | 'bank' | 'studio' | 'upload';

/**
 * The clobber-policy predicate: a BUILD may replace only what a build put
 * there (`'build'`/`'bank'`). A creator-owned asset — `'studio'`, `'upload'`,
 * or unstamped (pre-provenance) — is NEVER replaced by a build. ONE predicate:
 * the builder's attach guard and the iterate regen-set computation both key
 * off it.
 */
export function isBuildReplaceable(asset: Pick<CreationAsset, 'source'>): boolean {
  return asset.source === 'build' || asset.source === 'bank';
}

/** One ATTACHED asset on a creation (see {@link Creation.assets}). Attribution
 *  mirrors the version-doc convention: `attachedBy` is the member the action is
 *  attributed to, never a non-member admin. Timestamps are plain millis — the
 *  entry lives inside an array, where Firestore forbids serverTimestamp. */
export interface CreationAsset {
  /** Trusted inspection of the exact audio bytes; never browser-supplied metadata. */
  audio?: import('@djgg/core').AudioMediaMetadata;
  /** Vendor identity of a PROVIDER-GENERATED asset, carried from the candidate
   *  so provenance survives attachment. Absent on uploads, curated media and
   *  every asset whose bytes the platform's own pipeline drew — provider names
   *  live here and nowhere a creator-facing API can read them as an input. */
  generator?: AudioGeneratorProvenanceV1;
  variantId: string;
  /** Durable artifact-registry identity when this attachment was staged from a
   * reusable creation artifact. URL and digest are content evidence, but cannot
   * distinguish two accepted artifacts that intentionally share exact bytes. */
  sourceArtifactId?: string;
  /** SHA-256 of exact stored bytes, computed by trusted ingestion. Legacy/bank
   * assets may lack it; never infer identity from a name or delivery URL. */
  contentSha256?: string;
  kind: CreationAssetKind;
  /** Creator-given stable name ("tank", "arena-background") — how build and
   *  iterate prompts reference the asset. Slug-shaped (see isValidAssetName);
   *  unique among the creation's attached assets, case-insensitively
   *  (enforced in CreationsRepo.attachAsset's CAS). */
  name: string;
  /** Public https URL of the image in the creations bucket. */
  url: string;
  /** The prompt that generated it (provenance for regeneration). */
  prompt: string;
  /** Canonical pixel orientation recorded when this visual was produced.
   * Legacy assets omit it; consumers must not guess one from the filename. */
  orientation?: SpriteOrientationContract;
  attachedBy: Uid;
  attachedByHandle: string;
  attachedAt: number;
  /** Provenance of THIS attach (see {@link CreationAssetSource}). Absent on
   *  assets attached before provenance existed — read as creator-owned. */
  source?: CreationAssetSource;
  /** Present exactly when `kind === 'strip'`: the strip's cell geometry +
   *  identity (see {@link StripAssetMeta}), copied from the variant at attach. */
  strip?: StripAssetMeta;
}

/**
 * `creations/{id}/assetVariants/{variantId}` — one GENERATED asset variant
 * (pre-approval). Written by the builder's /assets/generate endpoint (trusted);
 * the attach flow reads it server-side to resolve the variant a member picked.
 * Clients never read this subcollection — the generate response carries the
 * variant list the studio renders.
 */
export interface AssetVariantDoc {
  /** Server-owned publication gate for asynchronous generation. Candidates
   * remain invisible until their owning job reaches `succeeded` in the same
   * transaction that flips this field to `active`. Missing legacy values are
   * read as active. */
  exposureState?: 'staged' | 'active';
  /** Audio shares the ordinary candidate/rejection/version lifecycle. */
  audio?: import('@djgg/core').AudioMediaMetadata;
  /** Vendor identity of a PROVIDER-GENERATED candidate (see
   *  {@link CreationAsset.generator}). Part of the candidate's immutable
   *  identity: the same variant id can never be re-recorded under another
   *  provider, model or request id. */
  generator?: AudioGeneratorProvenanceV1;
  variantId: string;
  /** Durable artifact-registry identity carried into an attachment selected
   * from this candidate. Absent on ordinary and legacy asset variants. */
  sourceArtifactId?: string;
  /** Exact post-process bytes. Optional only for legacy candidates. */
  contentSha256?: string;
  creationId: string;
  kind: CreationAssetKind;
  /** The name the creator gave the generation (see {@link CreationAsset.name});
   *  attach may override it. '' on variants that predate names. */
  name: string;
  url: string;
  /** Safety-screened, public pre-cut image retained for Creation Films only. */
  rawPreviewUrl?: string;
  prompt: string;
  /** Immutable orientation/alignment provenance for generated sprites. */
  orientation?: SpriteOrientationContract;
  /** Member the generation was attributed to. */
  createdBy: Uid;
  createdAt: number;
  /** The async generation job ({@link AssetGenJob}) that produced this variant
   *  — groups a job's variants for the studio recovery grid. Absent on uploads
   *  and variants that predate async generation. */
  genJobId?: string;
  /** Present exactly when `kind === 'strip'`: the generated strip's cell
   *  geometry + identity (see {@link StripAssetMeta}), stamped by the strip
   *  worker when it records the variant. */
  strip?: StripAssetMeta;
  /** Durable creator decision. Absent legacy rows are treated as `pending`. */
  reviewState?: 'pending' | 'attached' | 'skipped' | 'superseded' | 'rejected';
  reviewedAt?: number | null;
}

/**
 * `creations/{id}/viewers/{uid}` — existence == this user has viewed the game.
 * The marker that makes `distinctViewers` a distinct count. Server-written;
 * clients never read the viewer list (only the aggregate).
 */
export interface Viewer {
  at: number;
}

/** Per-version build lifecycle status. */
export type BuildStatus =
  | 'queued'
  | 'generating'
  | 'gating'
  | 'publishing'
  | 'live'
  | 'failed';

/** Gate results captured on a version. */
/** Private, unapproved source retained solely for a gated experimental repair. */
export interface FailedBuildCheckpoint {
  sourcePath: string;
  sourceDigest: string;
  constructionStage: 'core' | 'complete';
  failureText: string;
}

/** A browser-loadable, content-identified copy of one published web bundle.
 * This is deliberately narrower than DemoPlanV1: it pins the exact graphics
 * and code the passive live demo loads, but does not claim a deterministic
 * command tape, seed, duration, or replay verdict. */
export interface DemoRefV1 {
  schemaVersion: 1;
  creationId: string;
  version: number;
  /** SHA-256 of the canonical path/size/file-digest inventory for `dist/`. */
  artifactDigest: string;
  /** Public `index.html` under a digest-named, immutable object prefix. */
  url: string;
}

export interface VersionGates {
  /** Who ended this composed build from outside its worker. */
  terminatedBy?: 'cancel' | 'admin-fail' | 'reaper';
  /** Fail-closed retained runtime denial; operator control revision is evidence. */
  compositionRuntimeBlock?: { code: string; controlRevision: string | null };
  /** Exact maintained assembly and native full-loop evidence; not a provider
   * verdict or proof of interpreting arbitrary natural-language intent. */
  compositionProof?: { authorityDigest: string; sourceDigest: string; closureDigest: string; distDigest: string; nativeDigest: string; resolutionDigest: string };
  /** Composed media strips this version published without their own art —
   * exactly its final media receipt's `deferred` list (absent = none). Each
   * such state shows the kind's approved still sprite until a follow-up art
   * pass binds a regenerated strip. */
  deferredMedia?: CompositionDeferredMediaV1[];
  /** Complete-game revision evidence. Historical versions omit this; a saved
   * candidate is not complete until its exact bytes have been published. */
  completion?: {
    schemaVersion: 1;
    media: { complete: boolean; missing: string[] };
    gameplay: { complete: boolean };
    publication: { complete: boolean };
  };
  /** Builder-observed final manifest media bytes, including fallback assets.
   * Not proof against arbitrary image URLs embedded in generated source. */
  mediaInventory?: { schemaVersion: 1; complete: boolean; digests: string[]; sfx?: import('@djgg/core').CuratedSfxSpec };
  failedCheckpoint?: FailedBuildCheckpoint;
  acceptedRecovery?: { version: number; checkpoint: FailedBuildCheckpoint; sourceGeneration: string;
    measured?: { jobId: string; candidateId: string; checkpointRevision: number };
  };
  typecheck: boolean;
  tests: boolean;
  build: boolean;
  /** Free-form notes (gate output tail / failure reason). */
  notes: string;
  /** Bot-driven skilled-play progress metric (winnability gate) — the baseline
   *  the NEXT iterate's baseline-relative check compares against. null / absent
   *  = n/a (game declares no createBot, or predates the gate). */
  progressBaseline?: number | null;
  /** Rendered playtest audit verdicts (Tier 1 deterministic + Tier 2 VLM).
   *  Stamped once, for the candidate actually published, via the setStatus
   *  gates patch — the iterate no-regression baseline AND the public-flip
   *  stamp check read this. null / absent = the version predates the audit
   *  or the audit was off. */
  playtestAudit?: PlaytestAuditReport | null;
  /** SHA-256 (hex) of the `src.tgz` this version PUBLISHED (stamped on `live`
   *  beside `srcPath`). Lives in the gates jsonb because it is gate
   *  provenance: an art-completion copy build may inherit its seed version's
   *  sim-gate results ONLY when the archive it seeded from hashes to exactly
   *  this value (assert, never assume — completion gate reuse). Absent on
   *  versions that predate the field ⇒ a rebind on them runs the full
   *  battery. */
  srcSha256?: string;
  /** Completion gate reuse (art-completion copy builds only): the version
   *  whose sim-gate results this build INHERITED instead of re-running
   *  (src.tgz hash asserted, lever `DJGG_COMPLETION_GATE_REUSE`). Absent =
   *  every gate this version reports was run by this build. */
  gatesInheritedFrom?: number;
  /** Per-check verdicts for the NON-HARD gates (inert-resolve, fairness,
   *  winnability, …), keyed by gate name.
   *
   *  Only `typecheck`/`tests`/`build` used to reach the version doc, so from
   *  the database a gate that never RAN was indistinguishable from one that
   *  PASSED — you could not compute a base rate, and "no findings" quietly
   *  meant "no measurement". {@link GateCheckRecord.ran} makes that explicit. */
  checks?: Record<string, GateCheckRecord>;
  /** `gs://` URI of this build's MATCH TAPE — the sampled record of the match
   *  the audit drove (per-frame sim payloads + terminal state), filed beside
   *  `trace.json` in the PRIVATE logs bucket. Debugging data: a gate verdict
   *  says a criterion failed, a tape lets you ask why without re-running.
   *  Absent when none was recorded. */
  matchTapeGcsUri?: string;
  /** What this version's splash reel (the trailer's gameplay clip) came to.
   *  Stamped on every live publish since 2026-11-04; absent on older
   *  versions. Advisory: a skipped or static reel never blocks publication. */
  splashReel?: import('./splashReel.js').SplashReelOutcomeV1;
  /** Exact live-demo web artifact published by this build. Stored with gate
   * provenance in the existing JSON document; absent on legacy versions. */
  demoRef?: DemoRefV1;
  /** Immutable build-consumed review evidence, stored in the existing gates
   * JSONB. Absent on legacy versions; an empty reference string means the
   * build consumed no reference material. VersionsRepo updates attempts while
   * generating and freezes this snapshot before publication. */
  generationContext?: {
    editMode: 'create' | 'iterate' | 'copy';
    referenceMaterial: string;
    referenceMaterialOriginalLength: number;
    referenceMaterialSha256: string;
  };
  /** Immutable server-accepted lineage. Stored inside the existing gates JSONB
   * so rollout remains additive without a column/migration window. */
  acceptedLineage?: {
    baseVersion: number;
    /** Exact storage-object generation (decimal string; GCS generations may
     * exceed JavaScript's safe integer range). Required for platformer
     * iterations; empty for v1 and pre-provenance non-platformer work. */
    sourceArchiveGeneration: string;
    sourceArchivePath: string;
    sourceArchiveDigest: string;
    designRev: number;
    settingsRev: number;
  };
  /** Late auto-amend changed this version snapshot only; the newer creator
   * head remains authoritative. */
  reconciliationRequired?: boolean;
  /** Successful output was based on a source no longer active at finalize. */
  supersededLineage?: boolean;
  /** The generic platformer shell/scaffold contract this accepted build
   * requires. Old workers leave an unknown generation recoverable instead of
   * attempting to build against a different protected contract. */
  requiredPlatformerContractGeneration?: number;
}

/**
 * One non-hard gate's verdict on one build.
 *
 * `ran: false` is the important state: it means the gate was n/a for this
 * build (no bot, no play-trace, wrong build mode, disabled) and therefore says
 * NOTHING about quality. Averaging those in as passes is how a gate comes to
 * look healthier than it is.
 */
export interface GateCheckRecord {
  /** Did the check actually execute and produce a verdict? */
  ran: boolean;
  /** The verdict when it ran; null when it did not. */
  passed: boolean | null;
  /** Whether a failure was ENFORCED (vs recorded in shadow/warn). */
  enforced?: boolean;
  /** The enforcement ladder this gate sat at (`shadow` | `warn` | `fail` | …). */
  mode?: string;
  /** One-line human explanation — why it did not run, or why it failed. */
  note?: string;
}

/** Rollout mode of a playtest-audit tier (the winnability-gate ladder):
 *  `shadow` measures + stamps but never fails; `fail` enforces. */
export type PlaytestAuditMode = 'off' | 'shadow' | 'fail';

/** One named check inside a tier — per-criterion, evidence-bearing (the
 *  asset-judge verdict shape). */
export interface PlaytestAuditCriterion {
  pass: boolean;
  /** Short factual evidence ("score text 9px at 375px viewport"). Length-capped
   *  by the writer — this feeds prompts and the studio UI. */
  evidence: string;
}

/** One tier's outcome. `infra-open` = the ENVIRONMENT failed (browser launch,
 *  VLM transport), the tier fails OPEN and `reason` says why — a fault while
 *  executing the candidate is never infra-open (a hostile bundle must not
 *  convert the gate into an automatic pass). */
export interface PlaytestTierReport {
  mode: PlaytestAuditMode;
  verdict: 'green' | 'findings' | 'infra-open';
  criteria: Record<string, PlaytestAuditCriterion>;
  /** Reasons criteria were NOT judged. No entry here is a pass or a failure.
   * Absent on old reports = legacy/unknown coverage. Green only describes
   * judged criteria, never complete interaction verification. */
  coverage?: Record<string, string>;
  /** Platform-owned reason classes; model-authored coverage prose is never
   * execution authority. A missing prerequisite is not a transport failure. */
  coverageCodes?: Record<string, 'infrastructure' | 'prerequisite' | 'missing-evidence'>;
  /** Stable per-scenario results, without volatile coordinates or payloads.
   * Used to retain incremental repairs and detect semantic stagnation. */
  interactionOutcomes?: Array<{
    id: string;
    requirementIds: string[];
    device: 'desktop' | 'touch';
    pass: boolean;
  }>;
  /** Structured infra reason: timeout|transport|oom|unparseable|disabled|launch|webgl-env. */
  reason?: string;
  /**
   * Whether the candidate carried `test/player-interactions.json` at all.
   * `absent` is a THIRD state, distinct from a plan whose assertions failed
   * and from a broken instrument: primary-gameplay proof was never authored,
   * so there was nothing to execute. Games created before the plan entered the
   * builder contract carry none; combined with the build's seed baseline this
   * separates retrofit debt from a build that DELETED its seed's plan.
   * Absent on reports written before this field existed = unknown.
   */
  interactionPlan?: 'present' | 'absent';
  durationMs: number;
  /**
   * Which build of the AUDIT produced these criteria — see
   * {@link AUDIT_SCHEMA_VERSION} for what each value MEASURES.
   *
   * Criteria are only as trustworthy as the instrument, so two rows carrying
   * different values are two different readings and must never be pooled.
   * Absent = pre-versioning: at or before instrument `1`, and NOT internally
   * homogeneous — the drive changed unversioned inside that era (the menu
   * probe went from pressing Enter to clicking Play mid-corpus). Treat it as
   * its own bucket, never as `1`.
   */
  auditVersion?: number;
  /**
   * How far the drive got, beat by beat. Present even when the drive DIED:
   * the caller owns the array, so a report that ends `infra-open` still shows
   * which stage it reached instead of only a transport reason.
   */
  stages?: AuditStage[];
}

/** One completed beat of the audit drive. */
export interface AuditStage {
  /** Beat name: boot | menu | controls | end-screen | mobile-boot | gameplay
   *  | record (the landscape preview-clip leg — publish candidates only). */
  name: string;
  /** Wall time for this beat alone. */
  ms: number;
  /** Set when the beat ENDED the drive early — the reason it stopped. */
  stoppedBecause?: string;
}

/**
 * Bump whenever a change alters what the audit MEASURES (criteria added or
 * removed, a probe's method changed) — not for refactors that leave the
 * verdicts identical, and not for fields recorded ALONGSIDE the criteria
 * (`stages` times the drive; it reads nothing about the game). Consumers
 * compare base rates only within one value.
 *
 * Each value names its instrument, because the number is worthless if a
 * reader cannot tell what produced the row:
 *
 * 1 = the original sampler: textured display objects only, top scene level
 *     only. Blind to the shell's pre-art placeholders (`entityLayer.spawn`
 *     draws untextured Rectangles), so every primitive-drawn game reported no
 *     entities, no movement and dead controls while rendering and playing
 *     perfectly. Those readings are INVALID, not pessimistic.
 * 2 = the reconciled sampler (#306, 2026-08-01): every drawable including
 *     untextured ones, stable per-object ids, per-object px/s rates over
 *     normalised windows, scene + depth awareness, origin-corrected centres,
 *     tint/alpha sampling, and a held-tap pointer sweep for the games keys
 *     cannot drive.
 * 3 = `controls-effect` asks the SIM instead of the pixels (a88ed42): at or
 *     above SHELL_VERSION 0.14.0 the criterion reads `__DJGG__.inputStats()`
 *     and decides on the adapter's own command count. Below that floor the
 *     bundle cannot answer and it falls back to 2's rendered heuristic — so
 *     WITHIN 3, `controls-effect` is discriminated by the bundle's
 *     `shellVersion`, not by this number. The split self-clears as games
 *     rebuild.
 * 4 = the audit sees ART (2026-08-02): the sealed browser now serves the
 *     manifest's real assets at their original URLs (builder audit/mirror.ts),
 *     so every texture-dependent reading — Tier 1's `textures-resolved`,
 *     Tier 2's object-visibility/color-contrast/moderation — judges art for
 *     the first time instead of palette rectangles; 3's base rates are not
 *     comparable. Adds `art-bound` (the entity layer's own per-kind
 *     fallback-spawn count via `__DJGG__.renderStats()`, ≥ SHELL_VERSION
 *     0.16.0 — omitted below the floor, same discrimination note as 3) and
 *     Tier 2's `art-alignment`.
 * 5 = the shell renders at DISPLAY rate (2026-08-06, shell 0.21.0): entities
 *     are interpolated between revisions instead of stepping once per sim
 *     tick, and — the reason no 4-era number survives the boundary — the sim
 *     clock now advances on WALL time rather than Phaser's smoothed delta.
 *     Under 4, `tick-rate` was arithmetically a fraction of the container's
 *     frame rate, so every textured build failed it once the asset mirror
 *     made SwiftShader draw real art; the reading was about the audit
 *     container's GPU, not the candidate (docs/plans/2026-08-03-002 §3).
 *     `tick-rate` now samples the page's own rAF rate alongside the sim's and
 *     names a starved container as an environment finding; every
 *     motion-derived reading (`movement`, `controls-effect`'s rendered
 *     fallback, Tier 2's beat screenshots) is sampled from a differently-timed
 *     world. Pre-5 rates are not poolable with 5's — in the A/B, in the
 *     enforcement watch, or in any future flip, whose thresholds must be
 *     re-derived here.
 * 6 = `art-bound` covers the ZERO-SPAWN case (2026-08-21). Under 4 and 5 the
 *     criterion was OMITTED whenever the renderer counted no entity kinds, on
 *     the no-vacuous-pass rule — `entities-rendered` was said to own the empty
 *     scene. But an adapter that never implements `entities()` is NOT an empty
 *     scene: it hand-draws the whole game with Graphics primitives, so
 *     `entities-rendered` passes on a populated scene while every manifest
 *     binding sits inert. 13 live creations reported tier1 GREEN in exactly
 *     that state (shaftrunner: 7 declared kinds, 12 bound assets, none ever
 *     drawn). From 6 the gate reads `render-manifest.json` off the workspace
 *     and FAILS the criterion when the manifest declared kinds AND entity art
 *     but nothing spawned. A 5-era `art-bound` absence therefore means "not
 *     measured", a 6-era absence means "nothing to measure" — do not pool the
 *     two when counting pass rates.
 * 7 = the drive can actually find the PLAY button on shell 0.29.0
 *     (2026-08-27): `railButton` (packages/creation-shell/src/scenes/ui.ts)
 *     nests its label Text one level below an inner `face` Container, under
 *     the direct-child-only Text+Rectangle check `shellButtons()`
 *     (audit/drive.ts) used through 6 — so no menu row matched on ANY
 *     0.29.0 bundle, and `pickPlayButton`'s `menu[0]` fallback then clicked
 *     whatever container matched first (in practice the identity chip, a
 *     profile link), which read as `boot` failing with "menu handoff
 *     broken" on every 0.29.0 build regardless of whether the game's own
 *     menu worked. `shellButtons()` now matches a Text/interactive
 *     descendant at ANY depth (`buttonLabel`, audit/drive.ts) and the
 *     `menu[0]` fallback is gone — a bundle with no clickable play button
 *     correctly reports "no button found" instead of clicking an arbitrary
 *     element. `boot` and every criterion downstream of reaching PLAY read
 *     differently on a 0.29.0+ bundle before vs. after; pre-7 readings for
 *     those bundles are not poolable with 7's.
 * 8 = `art-bound` distinguishes explicit named platform built-ins from load
 *     failures (2026-09-01). Before 8, every entity without a successfully
 *     loaded external texture incremented `fallback`, so an intentionally
 *     authored `builtin:circle` was indistinguishable from a broken asset URL
 *     and failed the criterion. The shell now reports built-ins separately;
 *     emergency textures created after a load error remain `fallback` and
 *     still fail. Version 8 therefore measures the new explicit contract:
 *     generated/curated texture OR named built-in is valid, accidental
 *     fallback is not. Pre-8 `art-bound` rates are not poolable with 8's.
 */
// 9: intent-aware interaction/composition VLM criteria + explicit unjudged
// coverage. Screenshot-only action effects remain unverified, even on green.
// 10: real browser gestures must produce gameplay effects absent from an idle
// control covering the same simulation interval; VLM reviews executed primary
// interaction evidence. Critical proof remains mandatory through final repair.
// 11: desktop AND native touch consequences, 350ms visible-target reaction
// delay, and mandatory foreground visibility/terrain-scenery separation.
// Final materialized art participates in the existing candidate repair loop.
// Version 12: immutable coded-prototype stage defers final artwork/strip detail in visual review.
// Version 13: core-stage content scope and explicit visible-ground coverage/contact-edge review.
// Version 14: local host lifecycle guards abort the remainder of a rendered
// frame after generated callbacks dispose, pause, or otherwise invalidate it.
// This also applies to legacy audit sessions: subsequent steps and retained
// audit endpoints are no longer sampled from an invalidated world. Movement,
// tick-rate and interaction evidence can therefore differ from version 13.
// Do not pool the two instruments, even though criterion names are unchanged.
// Adds diagnostic animation-binding coverage from shell0.36.1: declared-state
// static fallbacks are explicit, without changing asynchronous-art enforcement.
// Shell0.36.2 renders shared score labels/secondary metrics, affecting sampled
// HUD content/legibility while keeping all scores and clocks host-authoritative.
// Version15: manifest7 anchors affect projected positions and visibility;
// legacy center/bottom inputs preserve their geometry.
// Version16 adds semantic rendered-control targeting for native pointer tests.
// Geometry follows visible platform controls; action effects and idle negatives
// remain mandatory. Older text/coordinate target failures are not comparable.
// Version17 polls through null startup snapshots instead of misclassifying
// a demo that is still starting as a failed mobile boot.
// Version18 integrates main's full accepted concept and change request context
// (up to 8,000 characters each), including requirements omitted by the old caps.
// Version19: version-pinned default showcase seeds replace wall-clock seeds;
// animation binding diagnostics also include semantic event-triggered playback.
// Criteria and pass thresholds are unchanged; retain the separate base-rate era.
// Version20: shared HUD now renders configured low-time warnings and canonical
// overtime announcements. This changes sampled HUD content/legibility, not the
// scoring/clock authority or criterion pass thresholds.
// Version21: pinned/configured measured demo playback, stable instance anchors,
// and neutral builtin masks/event-frame tinting change the world and pixels
// sampled by the audit. Keep this era separate from20; no criterion is relaxed.
// Version22: the VERDICT RULE changed, so these rows may not be pooled with21.
// A candidate that declares no `test/player-interactions.json` now reports that
// absence as its own state (`interactionPlan`) instead of an instrument fault,
// and the critical policy became a NON-REGRESSION rule: it withholds
// publication for proof the build's own source could already give, and records
// retrofit debt for proof that source never carried. Under21 an iterate on any
// pre-contract game failed terminally regardless of what it changed. No
// criterion threshold is relaxed and create builds are unaffected — every
// starter family ships a plan — but the pass/fail population for iterates on
// pre-contract games is different, which is exactly what this era separates.
// Version23: native evidence now reads renderer/collision/control geometry and
// structured actor/target/seat/event payloads from the shell's debug boundary.
// Input delivery also records idle quanta, raw channel transitions and actual
// sink verdict/order. Those are stricter, materially different observations;
// do not pool their interaction/geometry readings with version22.
// Version24: Tier-2 now reads up to twelve authored interaction scenarios
// instead of four and receives each scenario's exact requirement ids. Complex
// games can therefore be judged against every explicit core verb rather than a
// truncated steering/fire sample; do not pool those coverage verdicts with23.
// Version25: typed criterion provenance separates transport/prerequisite gaps
// from candidate failures; recovery audits stage visuals behind player proof
// and execute failed scenarios first without inheriting cross-bundle passes.
// V4 plans can carry24 independently observed desktop/touch scenarios. Exact
// bundle/plan fingerprints permit reusing the same complete approval, not a
// second vote. These coverage populations must not be pooled with version24.
// Explicit sprite contracts additionally require final-scene orientation,
// motion, firing and effect-origin consistency on desktop and native touch;
// legacy designs without contracts do not acquire fabricated proof debt.
// Version26: a seed-matched idle negative that reaches a real terminal loss is
// retained as frozen evidence instead of being mistaken for a missing sample.
// Structured-event idle evidence also uses the active action window, closing a
// false-credit gap when its complete event-log baseline starts at tick zero.
// Stalled, truncated or nonterminal controls remain failures.
// Version 27 captures browser-owned rendered frames continuously, starting
// on the ready menu, instead of blocking initial input on a screenshot call.
// Version 28 constrains the visual review to structured verdicts with bounded
// reasoning and rejects incomplete provider responses, even when JSON parses.
// Version29 adds measured complete-game terminal/restart proof. Earlier
// audit evidence cannot stand in for this stronger completion contract.
// Version30 adds native simultaneous key/control chords and rendered entity-ID
// targeting. These probe methods measure interactions that version29 could not
// deliver or identify, while retaining the same criteria and pass thresholds.
// Keep their coverage and interaction readings separate from the earlier era.
// Version 31 (2026-10-15): rendered samples now include each scene's main
// camera offset in node positions, and play-scene control targeting also
// accepts the shell touch-controls scene's visible control. Both change what a
// geometry/control probe reads; criteria and thresholds are unchanged.
// Version 32 (2026-11-01, #29f40338c): composed v4 plans can declare a
// `state-diverges` predicate, judged against the seed-matched idle match at
// the same ticks (equal before any gesture, different at the end). It passes
// interactions on values that also move by themselves, which no earlier
// predicate could credit, so interaction-effect readings are not poolable
// with version 31. Reused audit approvals (criticalAudit's hash) restart.
// Version 32 also covers (2026-11-04 bundle, before it shipped): every
// payload and event predicate is judged by the shared input-caused-divergence
// judge over the host's per-tick trace where the shell records one.
// Version 33 (2026-11-04): the mobile rendered drive reads the shell's native
// surface geometry and fails when any touch-control rectangle intersects the
// gameplay rectangle. Control/gameplay separation is now measured directly;
// authored entity keep-outs no longer substitute for the shell invariant.
export const AUDIT_SCHEMA_VERSION = 33;

/** The rendered playtest audit stamped on {@link VersionGates.playtestAudit}. */
export interface PlaytestAuditReport {
  tier1: PlaytestTierReport | null;
  tier2: PlaytestTierReport | null;
  /** Creator-visible quality notes composed from unresolved findings — shown
   *  in the studio and pre-seeded (advisory) into the next iterate prompt.
   *  null = the published candidate had none. */
  notes: string | null;
}

/** Token accounting for a version's LLM generation. */
export interface TokenUsage {
  input: number;
  output: number;
}

/**
 * One attached asset as recorded on a version doc's
 * {@link CreationVersion.assetsSnapshot} — the minimal identity needed to
 * diff the attachment inventory between two builds (same name + different
 * variantId = replaced in place).
 */
export interface VersionAssetRef {
  contentSha256?: string;
  name: string;
  variantId: string;
  kind: CreationAssetKind;
}

/**
 * The creator-facing digest of a version's render-manifest.json, stamped by
 * the builder at publish so the portal can show the game's VOCABULARY (which
 * entity kinds exist, which sprite keys they draw with, which attached-asset
 * names are bound) without fetching the gs:// manifest blob.
 */
export interface ManifestSummary {
  /** Current-renderer defaults; absent on legacy builds, never guessed by Studio. */
  presentation?: {
    manifestVersion: number;
    anchors: Record<string, import('@djgg/core').PresentationAnchor>;
    /** Visible-subject rectangles in normalized texture coordinates. Absent
     * means the published artwork has not been marked yet, not a guessed box. */
    visualBounds?: Record<string, { x: number; y: number; w: number; h: number }>;
    instanceAnchors?: Record<string, { entityKind: string; anchor: import('@djgg/core').PresentationAnchor }>;
    /** Exact base-image bindings from this version's manifest, not working-copy assets. */
    artwork?: Record<string, string>;
  };
  /** Builder-derived static cue declarations/calls in this exact version. */
  soundCues?: import('./soundCues.js').SoundCueCatalog;
  /** Entity kind → its `sprite` key (the name an attached asset must carry
   *  to reskin that entity). */
  entities: Record<string, string>;
  /** The background's bound sprite name, or null when it renders from the
   *  palette. */
  backgroundSprite: string | null;
  /** Names bound in the manifest's `assets` map (attached-asset names the
   *  build actually wired in). */
  boundAssets: string[];
  /** Entity kind → its declared animation state names (the keys of the
   *  manifest's per-entity `frames` maps; only animated kinds appear). Absent
   *  on summaries stamped before manifest v2 — guard the field. */
  animations?: Record<string, string[]>;
  /** The roles actually visible in the authored showcase scene. This is the
   * authoritative picker for visual changes; unlike `assets`, it excludes
   * historical/unbound art. Older builds omit it safely. */
  activeVisuals?: Array<{
    key: string;
    label: string;
    kind: 'background' | 'entity';
    assetName: string | null;
  }>;
  /** One creator-facing projection of the visual facts in the active render
   * manifest. Unlike {@link activeVisuals}, this includes bound animation
   * strips and makes non-asset rendering explicit, so Studio never has to
   * guess whether an attached file is actually visible to players. */
  visualUsage?: ManifestVisualUsage[];
}

export type VisualRenderMode = 'asset' | 'background-baked' | 'procedural' | 'ui' | 'none';

/** A visible runtime role and the art (if any) its active build actually uses. */
export interface ManifestVisualUsage {
  /** Stable renderer-owned role key, e.g. `entity:tank` or `background`. */
  key: string;
  /** Creator-facing label. */
  label: string;
  /** How this role is rendered by the active build. */
  renderMode: VisualRenderMode;
  /** Bound static asset name when {@link renderMode} is `asset`. */
  assetName: string | null;
  /** Bound animation state → strip asset name. Unbound declarations are omitted. */
  animations: Array<{ state: string; assetName: string }>;
  /** Declared states with no bound strip geometry in this exact version.
   * Absent on older summaries (unknown), not evidence that all art is ready. */
  missingAnimations?: Array<{ state: string; assetName: string }>;
  /** The current authored showcase scene can surface this role to creators. */
  visible: boolean;
  /** Reserved for a future explicit semantic alias; absent means Studio must
   * not invent a cross-role relationship. */
  sharedWith?: string;
}

/** A creator-approved, explicit exception to normal incremental asset reuse.
 * Names are stable manifest/asset role names, never opaque variant ids. */
export interface VisualMigration {
  assetNames: string[];
  summary: string;
  approvedAt: number;
}

/**
 * Build-time asset-generation provenance, stamped on the version doc so a
 * degraded build is visible instead of silently shipping placeholders: which
 * planned assets were generated, which came up empty (the shell draws its
 * rectangle fallback for those), and whether the platform injected the
 * manifest bindings for them.
 */
/** VLM quality-judge provenance for one build's generated assets (PR-F +
 *  Phase-B keep-best): which planned names passed as generated, which a retry
 *  fully fixed, which shipped as the best generated candidate that still beats
 *  the bank placeholder, and which failed outright (those names also appear in
 *  {@link VersionAssetGen.missing} / `defaulted` — the bank floor takes over). */
export interface VersionAssetGenJudge {
  /** Names whose first generation passed the rubric (includes fail-open
   *  accepts when the judge was unavailable — transport failure or a
   *  no-verdict safety-blocked reply; never discard art for an API blip). */
  passed: string[];
  /** Names where an improve-pass retry FULLY passed the rubric and shipped. */
  improved: string[];
  /** Names shipped as the best-scoring generated candidate that BEATS bank —
   *  the right, clean subject (passes the core criteria) even though a softer
   *  criterion (styleMatch/noText) never fully passed. Absent when empty (older
   *  provenance predates the keep-best loop). */
  partial?: string[];
  /** Names still worse than a bank archetype after the improve loop — degraded
   *  to missing/bank. */
  rejected: string[];
}

export interface VersionAssetGen {
  /** Creation asset names that generated + attached successfully. */
  generated: string[];
  /** Planned asset names that produced no usable image (degraded). */
  missing: string[];
  /** Names from {@link missing} the platform filled from the default sprite
   *  bank — the always-available archetype floor. The library stays fully
   *  populated + replaceable; the studio surfaces these as placeholders to
   *  regenerate. */
  defaulted?: string[];
  /** True when the platform injected the name→url bindings (and the entity
   *  sprite keys it could match) into the published render-manifest. */
  injected: boolean;
  /** VLM quality-judge outcome (absent when the judge is switched off). */
  judge?: VersionAssetGenJudge;
  /** Planned asset names the build SKIPPED because a creator-owned asset
   *  (studio/upload/pre-provenance — see {@link isBuildReplaceable}) holds the
   *  name: the iterate regen set never generates over one, and the attach-time
   *  clobber guard never replaces one. */
  preserved?: string[];
  /** Degradation provenance — present whenever generation INFRA failed the
   *  run: abort / pre-CLI failure, a total CLI-step death (crash/timeout), or
   *  the judge improve-retry's CLI death. Distinguishes "degraded because the
   *  infrastructure died" from a clean judge-rejected bank fallback, which is
   *  otherwise invisible from the DB. */
  note?: string;
  /** Post-edit closure provenance. `inferred` names visuals introduced by the
   *  final manifest but absent from the design-time plan; those assets publish
   *  on curated art and ride async completion. `materialized` confirms the
   *  binding bytes were copied into the immutable version bundle. */
  closure?: {
    inferred: string[];
    builtins: string[];
    materialized: string[];
    droppedStrips: string[];
    designReconciliationPending: boolean;
  };
}

/**
 * `creations/{id}/versions/{n}` — one build attempt/iteration. `n` is a
 * monotonic integer (1-based); {@link VersionsRepo.append} enforces it.
 */
export interface CreationVersion {
  /** Additive Creation Films edit manifest for this exact immutable version.
   * Visibility is inherited from the parent creation/version policy. */
  creationFilmManifest?: import('./creationFilms.js').CreationFilmManifestV1 | null;
  /** Public-safe exact launch projection for a Composition V2 version. Private
   * publication evidence remains in server-only records. */
  compositionPublicLaunchV2?: import('./compositionPublicPublicationV2.js').CompositionPublicLaunchProjectionV2;
  /** Explicit bridge from one exact published verified-core-v2 legacy
   * contract into a reviewed maintained composition. The old active version
   * remains live until this candidate passes normal composed publication. */
  compositionConversion?: CompositionConversionV1;
  /** Nullable pointer to private verified-core@2 authority. The browser sees
   * only this opaque id and the sanitized summary, never admission inputs. */
  qualityRunId?: string | null;
  qualitySummary?: {
    recipe: 'verified-core@2';
    phase: string;
    proofDebtCodes: string[];
  } | null;
  /** Immutable accepted recipe. Absence denotes the original current pipeline. */
  pipelineSnapshot?: BuildPipelineSnapshot;
  /** Additive dormant verified-core state. Absence is historical/legacy and is
   * never interpreted as quality certification. Accepted inputs are immutable;
   * stage/spec transitions are server-owned and claim fenced. */
  qualityRun?: AcceptedQualityRunV1 | null;
  /** Server-owned acceptance classification. Absent is a historical writer;
   * readers preserve that fact instead of inventing an exemption or pass. */
  executionPolicy?: AcceptedBuildExecutionPolicyV1 | null;
  /** Monotonic version number (mirrors the doc id `{n}`). */
  n: number;
  prompt: string;
  provider: string;
  model: string;
  /** When a build FELL BACK to a different engine than the creator chose (the
   *  requested {@link provider} wasn't keyed at build time), the provider/model
   *  it ACTUALLY ran on. Absent on the normal path where it ran as requested —
   *  the builder records this rather than silently swapping models (R3-6). */
  actualProvider?: string;
  actualModel?: string;
  diffSummary: string;
  buildStatus: BuildStatus;
  /** The builder's live progress pointer (phase + round/gate/pass detail),
   *  mirrored from its `progress` trace steps by VersionsRepo.setProgress —
   *  the studio's phase tracker. Absent until the worker reports; the LAST
   *  value stays after the terminal transition (a failed build shows where it
   *  stopped). null = stored as SQL NULL. */
  buildProgress?: BuildProgress | null;
  /** Durable creator-facing timing for this run. Queue intervals are recorded
   * by the queue claim/requeue path; the celebrated build duration excludes
   * exactly those intervals. Absent on historical versions means unavailable
   * — consumers must never infer or fabricate it from phase positions. */
  buildTiming?: BuildTiming | null;
  /** Immutable fan-out accepted with this build. Media job ids are
   * deterministic children of the command id, so a lost response or repeated
   * Build click returns the same work instead of scheduling duplicates. */
  creationRun?: CreationRunIntent | null;
  gates: VersionGates;
  /** gs:// path of the source tarball for this version. */
  srcPath: string;
  /** gs:// path of the built web bundle for this version. */
  bundlePath: string;
  /** gs:// path of the emitted render-manifest.json. */
  renderManifestPath: string;
  /** Creator-facing digest of the published manifest (see
   *  {@link ManifestSummary}). Stamped on `live` transitions; absent on
   *  failed builds and versions that predate it. */
  manifestSummary?: ManifestSummary;
  /** How this build was initiated. `fork-remix` seeds parent source but runs
   * the normal LLM/edit pipeline; legacy forks remain `copy`.
   * `presentation-republish` rebuilds the active version's unchanged game
   * with zero model calls so build-time presentation is current. */
  buildMode?: 'copy' | 'fork-remix' | 'visual-remix' | 'asset-rebind' | 'presentation-republish';
  presentationEdit?: import('./presentationEdit.js').PresentationEditV1;
  /** Selected visual roles whose generated art may replace build-owned art. */
  visualMigration?: VisualMigration;
  /** Build-time asset-generation provenance (see {@link VersionAssetGen});
   *  absent when the build ran no plan generation. */
  assetGen?: VersionAssetGen;
  /** Per-mechanic outcome for a creator-first platformer build. Constraints
   *  are advisory provenance and never mutate the retained GameDesign. Null
   *  denotes a pre-existing or non-platformer version in Postgres. */
  platformerMechanicOutcomes?: unknown[] | null;
  /** Structured, non-blocking primary-actor art problems for Studio repair.
   *  Null denotes a pre-existing or non-platformer version in Postgres. */
  platformerPrimaryActorAssetIssues?: unknown[] | null;
  /** gs:// path of the headless sim bundle (`sim.mjs`) the online host loads
   *  (Phase 2). Absent when the sim artifact wasn't published (a build that
   *  predates it, or the opportunistic artifact step failed). */
  simPath?: string;
  /** Optional trusted runtime-contract sidecar metadata. Null/absence is a
   * legacy version and is deliberately never inferred or backfilled. */
  runtimeContract?: RuntimeContractRefV1 | null;
  /** Explicit capability authority captured when this build was enqueued.
   * Existing versions without it remain legacy; readers never infer a lock
   * from a source path, active pointer, or mutable creation head. */
  capabilityLock?: GameplayCapabilityLockV1 | null;
  /** Optional static poster retained for versions that already published one. */
  posterUrl?: string | null;
  /** Optional recorded clip for this exact immutable version (0036 column).
   * Absent means no verified clip; promotion must not inherit an older one. */
  previewUrl?: string | null;
  /** The @djgg/creation-shell version this bundle was built against (A4).
   *  Absent on versions that predate the shell. */
  shellVersion?: string;
  /** The shell's adapter-API generation (bumps only on a breaking
   *  CreationAdapter change — a prompt + migration event). */
  shellApi?: number;
  tokenUsage: TokenUsage;
  /**
   * Estimated USD for this build's LLM edit rounds, ACCUMULATED PER CALL at
   * the rate of the model that actually served each one, then frozen here.
   *
   * Stamped rather than derived on read for two reasons. It is the platform's
   * single source of cost truth (the studio Overview tile read
   * `tokenUsage × current rate` until 2026-07-29, so it silently disagreed
   * with every other surface the moment a rate was corrected). And summed
   * tokens cannot be priced correctly after the fact anyway: a build that
   * fails over to OpenAI mid-run has rounds at two providers' rates, which one
   * `actualModel` applied to the total can never reconstruct.
   *
   * 0 on versions built before this field (backfilled by
   * tools/ops/backfill-llm-costs.mjs) and on unpriced models.
   */
  costUsd: number;
  /** The COMPLETE settings this build ran against (dense — the compiler
   *  input; see spec/validate.ts completeSettings). Immutable once stamped by
   *  requestBuild; absent on versions that predate the wizard. */
  settingsSnapshot?: GameSettings;
  /** Schema version of {@link settingsSnapshot}. */
  settingsSchemaVersion?: number;
  /** The creation's `settingsRev` at requestBuild time — the baseline the
   *  pending-changes banner compares against. */
  settingsRev?: number;
  /** Compiler-emitted settings diff vs the last built snapshot ('' on the
   *  first build or when nothing changed). */
  settingsDiff?: string;
  /** The design doc this build ran against — stamped by requestBuild from the
   *  ACTIVE version's snapshot ?? the creation head, so rollback + iterate
   *  fork the design lineage exactly like the source lineage. Immutable once
   *  the build finishes (the iterate auto-amend patches it pre-build). Absent
   *  on versions that predate concept-first creation. */
  designSnapshot?: GameDesign;
  /** The creation's `designRev` this snapshot corresponds to. */
  designRev?: number;
  /** One-line record of the iterate auto-amend applied for THIS build (''
   *  or absent = the change request implied no design change). Rendered in
   *  the version history. */
  designAmendment?: string;
  /** The creation's attached-asset inventory at enqueue time ([] when nothing
   *  was attached — always stamped, unlike the settings snapshot, because the
   *  inventory is always known). The studio's iteration history diffs
   *  consecutive snapshots into added/updated/removed. Absent on versions
   *  that predate the field. */
  assetsSnapshot?: VersionAssetRef[];
  /** gs:// URI of the enrichment design brief in the PRIVATE llm-logs bucket
   *  ('' when the enrichment pass was skipped or failed). Never the brief
   *  text itself — version docs are public-read and the brief is invisible
   *  by product decision; superadmins read it via resolveLlmPromptUrl. */
  designBriefGcsUri?: string;
  /** gs:// URI of this build's trace.json in the PRIVATE logs bucket
   *  (build-traces/ prefix). Stamped on both terminals (live AND failed) by
   *  the worker that owns the terminal setStatus; absent on builds that
   *  predate tracing or whose upload failed. The trace embeds prompt
   *  previews, so it is served role-tiered via getBuildTraceFn, never read
   *  client-side. */
  traceGcsUri?: string;
  /** gs:// URI of this build's debug-bundle.json, beside the trace under the
   *  same attempt key. Present ONLY on builds that failed at least one gate
   *  round (the bundle is not written otherwise), so its presence is itself the
   *  "this build burned repair rounds" signal. Same private bucket + read
   *  posture as traceGcsUri — it embeds full source bodies. */
  debugBundleGcsUri?: string;
  /** Set on deterministic asset-rebind builds: the version this zero-model
   *  copy build SEEDS FROM and re-binds onto. Most are platform-initiated art
   *  completion waves; `buildMode:'asset-rebind'` is the creator-triggered
   *  equivalent. Its presence routes the copy path through asset finalize,
   *  protects the active pointer from a newer creator build, and suppresses
   *  fork-divergence side effects. */
  artCompletionOf?: number;
  /** Assets a deterministic rebind publishes — captured at enqueue so the
   *  build is self-contained (no queue reads at claim time). */
  artCompletionAssets?: Array<{
    contentSha256?: string;
    variantId: string;
    name: string;
    kind: string;
    url: string;
    prompt: string;
    entityName?: string;
    /** Present when `kind === 'strip'`: the strip variant's cell geometry +
     *  identity, carried so the completion build binds the manifest `strips`
     *  entry without a variant re-read. */
    strip?: StripAssetMeta;
  }>;
  /** Uid this build is ATTRIBUTED to — the requesting member, or the
   *  collaborator a superadmin attributed the build to (never the admin's own
   *  uid; see the attribution invariant in creatorHandlers). Empty on legacy
   *  versions that predate attribution. */
  updatedBy: string;
  /** Denormalized display handle for {@link CreationVersion.updatedBy}. */
  updatedByHandle: string;
  createdAt: number;
  /** Set when the build reaches a terminal status (live/failed). */
  finishedAt: number | null;
}

export interface BuildQueueInterval {
  /** When this version entered (or re-entered) the builder queue. */
  enteredAt: number;
  /** When a worker claimed it. Null while queued, and when a terminal build
   * ended without another claim. */
  claimedAt: number | null;
}

export interface BuildTiming {
  /** Build approval/queue acceptance time. */
  acceptedAt: number;
  /** First worker claim. Null until a worker actually starts. */
  workerStartedAt: number | null;
  /** Terminal readiness/failure time. Null while the run is active. */
  readyAt: number | null;
  /** Every observed builder-queue interval, including requeues. */
  queueIntervals: BuildQueueInterval[];
  /** Sum of completed queue waiting. Active views also include an open interval
   * up to their observation time; terminal records freeze it at `readyAt`. */
  queueMs: number;
  /** `readyAt - acceptedAt - queueMs`, frozen at terminal. */
  buildDurationMs: number | null;
  /** `readyAt - acceptedAt`, frozen at terminal. */
  totalElapsedMs: number | null;
}

export interface CreationRunIntent {
  runId: string;
  /** What the creator asked for that this game doesn't do, when the run fell back
   * to a stock game. Absent on every other game. */
  notInYet?: string[];
  approvedDesignRev: number;
  acceptedAt: number;
  codeJobId: string;
  /** Independent visual-plan generation. Null means no visual plan was needed. */
  visualJobId: string | null;
  /** Independent generated soundtrack. Null means generation was disabled or
   * the approved pitch did not request music. */
  musicJobId: string | null;
  /** Existing media work reused by a repair of the same approved design. The
   * ids above remain the join authority; this explains why this version did
   * not enqueue or re-charge those branches. */
  reusedMedia?: {
    /** Legacy fallback for runs written before per-branch origins. */
    sourceVersion: number;
    /** Original immutable job versions; visual and music can come from
     * different attempts. When present, every reused branch must be named. */
    sourceVersions?: Partial<Record<'visual' | 'music', number>>;
    visual: boolean;
    music: boolean;
  };
  /** Member-readable reason why the approved run intentionally has no paid
   * music job. Quota reasons are chosen atomically while accepting the run. */
  musicDefaultReason?:
    | 'generation-disabled'
    | 'not-requested'
    | 'direction-unchanged'
    | 'creation-lifetime-cap'
    | 'creator-daily-cap'
    | 'platform-daily-cap';
  /** Procedural SFX are supplied by the pinned local bank, never an AI job. */
  proceduralSfx: 'included' | 'not-required';
}

/**
 * `creations/{id}/likes/{uid}` — existence == like. The count is mirrored onto
 * {@link Creation.likesCount} by a function; this doc is tiny and owner-only.
 */
export interface Like {
  uid: Uid;
  createdAt: number;
}

/** Who recorded a {@link CreationFeedback} row: the creation's creator, one of
 *  its editors, or a superadmin evaluating a game they are not a member of
 *  (an ADMIN row is never a member's opinion — `attributeTo` does not vote). */
export type CreationFeedbackRole = 'creator' | 'editor' | 'admin';

/** The one-tap verdict: keep iterating on this version, or discard it.
 *  "discard" is feedback only — nothing is deleted. */
export type CreationFeedbackVerdict = 'keep' | 'discard';

/** Bound on the optional free-text note. */
export const FEEDBACK_NOTE_MAX_LEN = 280;

/**
 * `creations/{id}/feedback/{versionN}:{uid}` — one respondent's STATED INTENT
 * about one live version they played (plan Unit 1b: the platform-wide "kept vs.
 * throwaway" signal). Server-written by `recordCreationFeedback`; revisable
 * (same doc, `revisedAt` moves, `createdAt` stays). Family / recipe / engine
 * are NOT stamped: Admin Stats joins them at read time.
 */
export interface CreationFeedback {
  versionN: number;
  respondentUid: Uid;
  respondentRole: CreationFeedbackRole;
  verdict: CreationFeedbackVerdict;
  note: string;
  /** The version's bundle path when the verdict was recorded — pins what was played. */
  bundlePath: string;
  /** True when a live art-completion build of this version existed at record time. */
  artComplete: boolean;
  createdAt: number;
  revisedAt: number;
}

/**
 * `creations/{id}/comments/{commentId}` — one player comment on a GAME (a
 * creation as a whole, not a version). Written ONLY by the post/delete callables
 * (the rules deny direct client writes, R3-9), which move the doc and the
 * mirrored {@link Creation.commentsCount} together. `handle` is snapshotted at
 * post time so a row survives a later handle change; `uid` is retained so the
 * author (and superadmins) can delete it.
 */
export interface Comment {
  uid: Uid;
  /** Author's display handle, resolved server-side at post time. */
  handle: string;
  /** The comment text (trimmed, length-bounded by the callable). */
  body: string;
  createdAt: number;
}

/** Lifecycle of an Arena match invite. `open` is the ONLY discoverable state;
 *  `accepted` / `cancelled` / `expired` are terminal (the actual room pairing
 *  happens off-DB once accepted — the DB can't observe the Colyseus room). */
export type MatchInviteStatus = 'open' | 'accepted' | 'cancelled' | 'expired';

/**
 * `matchInvites/{token}` — an online-match challenge for a creation (the Arena).
 * Written ONLY by the createMatchInvite / acceptMatchInvite / cancelMatchInvite
 * callables; clients READ for discovery and never write (RLS denies it). The
 * `token` doubles as the Colyseus pairing key both players carry in
 * `/play/<creationId>/<version>/?match=<token>`; the Arena is a PUBLIC board, so
 * the token is a public pairing key (any authed user can read an open invite's
 * token), not a private capability. `version` is PINNED at creation so host +
 * guest join the identical version's room — filterBy is exact, and a drift
 * would silently split them into two rooms.
 */
export interface MatchInvite {
  /** The pairing key / invite id (unguessable bearer capability). */
  token: string;
  creationId: string;
  /** Pinned creation version — both players join THIS exact version's room. */
  version: number;
  /** The challenger — seated first, so player 1 / slot 0. */
  hostUid: Uid;
  hostHandle: string;
  status: MatchInviteStatus;
  /** The accepting player — seated second, so player 2 / slot 1 (set on accept). */
  guestUid?: Uid;
  guestHandle?: string;
  createdAt: number;
  /** Epoch-ms TTL: an open invite past this is swept to `expired` and hidden
   *  from discovery. */
  expiresAt: number;
}

/**
 * `userPresence/{uid}` — platform-wide social presence: where a signed-in user
 * is right now and when they were last seen. Written ONLY by the
 * `heartbeatPresence` callable, beaten by all three runtimes (portal SPA,
 * natives' @djgg/game-client, creations' @djgg/creation-shell); clients READ
 * for the home page's Online rail and never write (RLS denies it).
 *
 * `handle` + `avatarKey` are denormalized from the user's {@link CreatorProfile}
 * on every beat (the {@link MatchInvite.hostHandle} precedent) so the rail
 * renders from one stream with no join. Unclaimed profiles (no handle) are
 * never written.
 *
 * The game fields are set together or not at all: `gameKey` is the creation id
 * / native slug the user is playing (the {@link UserGameFinish.gameKey}
 * vocabulary), `gameTitle`/`gameSlug` its SERVER-resolved label — a client can
 * never inject a title into the public rail. All absent = browsing the portal.
 *
 * Freshness is the consumer's job: a row that merely goes stale emits no
 * realtime event, so {@link import('./presence/status.js').presenceStatus}
 * re-derives online/last-seen from `lastSeenAt` against the viewer's clock.
 */
export interface UserPresence {
  uid: Uid;
  /** Display handle, re-stamped from the profile every beat. */
  handle: string;
  /** PROFILE_AVATARS catalog id ('' if unchosen). */
  avatarKey: string;
  /** Creation id / native slug being played; absent = browsing the portal. */
  gameKey?: string | null;
  /** Server-resolved title of `gameKey` (never client-supplied). */
  gameTitle?: string | null;
  /** Server-resolved slug of `gameKey` — the `/g/:slug` link target. */
  gameSlug?: string | null;
  /** Epoch-ms of the most recent beat. */
  lastSeenAt: number;
}

/**
 * `chatMessages/{id}` — one line in the Clubhouse, the platform's single global
 * ephemeral chat room (the social surface grown onto {@link UserPresence}'s
 * rail). Written ONLY by the `postChatMessage` callable; clients READ the live
 * room and never write (RLS denies it), and `deleteChatMessage` enforces
 * author-or-superadmin the way {@link Comment} deletion does.
 *
 * Read visibility is bounded to a 24h RETENTION window, and rows past it are
 * physically swept — that window IS the ephemerality (the transport is an
 * ordinary table, because the browser's Supabase client is anon-key only and a
 * Realtime-broadcast payload's identity would be client-asserted). Unlike the
 * public presence rail, the room is readable by signed-in accounts only.
 *
 * `handle` + `avatarKey` are denormalized from the author's
 * {@link CreatorProfile} at post time (the {@link UserPresence} precedent) so
 * the dock renders from one stream with no join, and a line keeps the identity
 * it was posted under. Unclaimed profiles (no handle) can never post.
 *
 * TEXT ONLY. A GIF kind was speced alongside it, but Google decommissioned the
 * Tenor API on 2026-06-30 and no replacement was adopted — so the fields had no
 * writer and were removed rather than shipped dead (0041's header carries the
 * same note). Emoji ride in `body` like any other character.
 */
export interface ChatMessage {
  uid: Uid;
  /** Author's display handle, resolved server-side at post time. */
  handle: string;
  /** PROFILE_AVATARS catalog id ('' if unchosen), stamped at post time. */
  avatarKey: string;
  /** The message (trimmed, length-bounded by the callable). */
  body: string;
  createdAt: number;
}

/**
 * `creations/{id}/viewShards/{0..9}` — sharded view counter. The portal
 * increments a random shard; a scheduled function sums them into
 * {@link Creation.viewsCount}.
 */
export interface ViewShard {
  count: number;
}

/** Number of view-counter shards (`viewShards/0` … `viewShards/9`). */
export const VIEW_SHARD_COUNT = 10;

/** Queue status of a build job. */
export type BuildJobStatus = 'queued' | 'claimed' | 'done' | 'failed';

/** Durable control-plane state for measured-first-playable@2. It lives on the
 * build job so checkpoint, reservation, claim and continuation transitions can
 * commit under one compare-and-swap. Historical jobs omit it. */
export type MeasuredBuildOperationKind =
  | 'seed'
  | 'prepare'
  | 'gate'
  | 'candidate-audit'
  | 'publication-audit'
  | 'checkpoint'
  | 'publish'
  | 'provider'
  | 'image-generation'
  | 'image-judge'
  | 'music-generation'
  | 'media-aux';

export interface MeasuredBuildReservationV1 {
  reservationId: string;
  operationId: string;
  kind: MeasuredBuildOperationKind;
  status: 'reserved' | 'settled' | 'uncertain';
  provider?: EngineRef['provider'] | 'lyria';
  model?: string;
  requestId?: string;
  reservedCalls: number;
  reservedCostMicros: number;
  actualCostMicros?: number;
  maxDurationMs: number;
  reservedAt: number;
  settledAt?: number;
  error?: string;
  /** Present for reservations made by an atomically accepted media child. */
  mediaJobId?: string;
  mediaClaimAttempt?: number;
  mediaWorkerId?: string;
}

export interface MeasuredBuildCandidateDistV1 {
  /** Immutable private archive retained for exact worker resume/debugging. */
  archive: ImmutableCandidateObjectV1;
  /** Individually immutable private objects usable by the isolated preview
   * gateway. Empty means the candidate is not preview-launchable. */
  inventory: CandidateInventoryEntryV1[];
}

export interface MeasuredBuildCandidateV1 {
  candidateId: string;
  createdAt: number;
  source: ImmutableCandidateObjectV1;
  dist?: MeasuredBuildCandidateDistV1;
  report: Record<string, unknown>;
  progress: Record<string, unknown>;
  runnable: boolean;
  remainingChecks: string[];
}

export interface MeasuredBuildCheckpointV1 {
  schemaVersion: 1;
  revision: number;
  savedAt: number;
  /** Opaque pipeline-owned, versioned state. The worker persists and restores
   * it byte-for-byte; pipeline.ts validates its own semantic schema. */
  executionState: Record<string, unknown>;
  currentCandidateId: string;
  bestCandidateId: string | null;
  candidates: Record<string, MeasuredBuildCandidateV1>;
}

export interface MeasuredBuildTraceSegmentV1 {
  segment: number;
  claimGeneration: number;
  uri: string;
  status: 'running' | 'yielded' | 'live' | 'failed' | 'aborted';
  startedAt: number;
  finishedAt: number | null;
}

export interface MeasuredBuildContinuationV1 {
  /** Deterministic `${jobId}:${checkpointRevision}` intent identity. */
  intentId: string;
  checkpointRevision: number;
  /** {@link measuredCheckpointProgress} of the retained checkpoint: what the
   * successor resumes from, and what its own yield must move beyond. */
  progress: string;
  state: 'needed' | 'dispatched';
  createdAt: number;
  dispatchedAt?: number;
}

/** Successful structural proof retained before any composition media child can
 * become claimable. Its identity is immutable for this logical build. */
export interface CompositionProofBarrierV1 {
  schemaVersion: 1;
  authorityDigest: string;
  checkpointRevision: number;
  candidateId: string;
  proofDigest: string;
  /** Immutable structural evidence, independent from later reports stored
   * under the same candidate ID in the mutable latest checkpoint. */
  structural: { candidateDigest: string; candidate: MeasuredBuildCandidateV1 };
  mediaBasisDigest: string;
  mediaJobIds: string[];
  committedAt: number;
  claimGeneration: number;
}

export interface CompositionFinalProofV1 {
  schemaVersion: 1;
  authorityDigest: string;
  structuralProofDigest: string;
  mediaBasisDigest: string;
  checkpointRevision: number;
  candidateId: string;
  candidateDigest: string;
  candidate: MeasuredBuildCandidateV1;
  receipt: import('./spec/gameSpec.js').CompositionMediaReceiptV1;
  receiptDigest: string;
  committedAt: number;
  claimGeneration: number;
}

/** Immutable handoff common to resolved and pending-generation admission. */
export type CompositionV2AcceptanceCommon = {
    sourcePresentationV2?: import('./spec/compositionSourcePresentationV2.js').CompositionSourcePresentationV2;
    route: 'private-composition-v2';
    buildId: string;
    authority: AcceptedCompositionV2;
    /** Absent exactly for a fresh version-one composition. */
    predecessorContext?: CompositionPredecessorContextV2;
    releaseId: string;
    admissionProfile: string;
    controlRevision: number;
    acceptedRegistry: Pick<ComponentRegistryStateV2, 'revision'> & { digest: string };
    predecessorCas: PublicationProofV2['predecessorCas'];
    /** Immutable fresh-public destination and serving policy. Absent on
     * historical, successor and explicitly private-only admissions. */
    completionTarget?: import('./compositionPublicCompletionV2.js').CompositionPublicCompletionTargetV2;
  };
export type CompositionV2ResolvedAcceptance = CompositionV2AcceptanceCommon & {
  schemaVersion: 2;
  resolution: CompositionResolutionV2;
};
/** The accepted miss is immutable. A completed materialization lives separately. */
export type CompositionV2GenerationAcceptance = CompositionV2AcceptanceCommon & {
  schemaVersion: 3;
  kind: 'bounded-component-generation';
  pendingResolution: import('./componentGenerationPlanV2.js').ComponentGenerationPlanV2;
  /** Original immutable resolver inputs; materialization never replaces them. */
  generationInputs: import('./spec/compositionV2.js').ImmutableV2<{
    registry: ComponentRegistryStateV2;
    boundaries: readonly import('./componentGenerationV2.js').ComponentGenerationBoundaryV2[];
  }>;
};
export type CompositionV2Acceptance = CompositionV2ResolvedAcceptance | CompositionV2GenerationAcceptance;

export interface MeasuredBuildExecutionV1 {
  /** Accepted composition identity; absence never downgrades recipe 4. */
  compositionDigest?: string;
  /** Exact digest of the closed static-media execution authority. Present
   * only for a fresh composed build with separately released media spend. */
  compositionMediaExecutionDigest?: string;
  /** Immutable composed successor identity. Present exactly for fork/rebind
   * recipe-4 jobs and matched against executionPolicy.compositionLineage. */
  compositionLineageDigest?: string;
  /** Immutable composed revision identity. Present exactly for a
   * `composition-revision` job, stamped at acceptance and matched against
   * executionPolicy.compositionRevision. */
  compositionRevisionDigest?: string;
  /** Server-only durable handoff for the private Composition V2 worker. It is
   * derived during trusted fresh or successor admission and never accepted
   * from browser JSON or copied onto the public version document. */
  compositionV2Acceptance?: CompositionV2Acceptance;
  /** Conservative generation exposure; external receipts never rewrite this ledger. */
  generationExposure?: import('./componentGenerationExposureV2.js').ComponentGenerationExposureV2;
  compositionBarrier?: CompositionProofBarrierV1;
  compositionFinalProof?: CompositionFinalProofV1;
  schemaVersion: 1;
  /** Monotonic compare-and-swap fence for whole-document JSON updates. */
  ledgerRevision?: number;
  recipe: { id: 'measured-first-playable'; revision: 2 | 3 | 4 | 5 };
  acceptedAt: number;
  /** `windowStart + limits.maxWallTimeMs`. A composed build's window starts
   * at the claim of its first request segment (`startCompositionWindow`);
   * until then this is provisional (acceptance + window). */
  deadlineAt: number;
  limits: {
    maxWallTimeMs: number;
    maxProviderCalls: number;
    maxCostMicros: number;
    finishReserveMs: number;
  };
  usage: {
    reservedProviderCalls: number;
    settledProviderCalls: number;
    reservedCostMicros: number;
    settledCostMicros: number;
  };
  reservations: Record<string, MeasuredBuildReservationV1>;
  checkpoint?: MeasuredBuildCheckpointV1;
  traceSegments: MeasuredBuildTraceSegmentV1[];
  continuation?: MeasuredBuildContinuationV1 | null;
}

/** Authority copied onto a creation-run media child in the same transaction
 * that creates its parent measured build; a composed parent's first segment
 * claim moves `deadlineAt` with its own (`startCompositionWindow`). Provider
 * reservations still live only on the parent ledger; this pointer carries no
 * independent allowance. */
export interface MeasuredMediaBudgetAuthorityV1 {
  schemaVersion: 1;
  buildJobId: string;
  acceptedAt: number;
  deadlineAt: number;
  /** Required together for a paid composed static-media child. Legacy shared
   * media budgets omit all three fields. */
  role?: 'composition-media@2';
  authorityDigest?: string;
  executionDigest?: string;
}

/**
 * `presentationRepublishRequests/{id}` — a reviewed request to re-publish a
 * game's active version unchanged (migration 0146). Written only by the
 * backfill capability; settled only by the build reaper through
 * `CreationsRepo.acceptPresentationRepublish` / `refusePresentationRepublishRequest`.
 */
export interface PresentationRepublishRequest {
  creationId: string;
  reason: 'title-parity';
  expectedActiveVersion: number;
  requestedBy: 'github-actions';
  status: 'pending' | 'accepted' | 'refused';
  outcome?: string | null;
  acceptedVersion?: number | null;
  createdAt: number;
  settledAt?: number | null;
}

/**
 * `buildJobs/{jobId}` — the build queue. A builder worker CLAIMS a queued job
 * (transactional flip to `claimed` + `workerId` + `heartbeatAt`), heartbeats
 * while working, and completes/fails it.
 */
export interface BuildJob {
  creationId: string;
  /** Target version number this job builds. */
  version: number;
  status: BuildJobStatus;
  /** Set when claimed; null while queued. */
  workerId: string | null;
  /** Opaque per-claim authority. Worker identity alone is never sufficient for
   * quality-state mutation; a reclaim always receives a new token. */
  claimToken?: string | null;
  /** Monotonic generation incremented for every successful claim. */
  leaseGeneration?: number;
  /** Durable wall-clock expiry for this exact claim generation. */
  leaseExpiry?: number | null;
  /** Last heartbeat (server millis); null while queued. */
  heartbeatAt: number | null;
  /** Current queue-entry time. Present on new jobs; absent on legacy rows. */
  queueEnteredAt?: number | null;
  /** Claim timestamp for the current/last attempt. */
  claimedAt?: number | null;
  /** Closed queue intervals accumulated across requeues. */
  queueIntervals?: BuildQueueInterval[];
  /** Sum of {@link queueIntervals}; cached for status reads. */
  queueMs?: number;
  createdAt: number;
  /** Set when the job reaches a terminal status. */
  finishedAt: number | null;
  /** Failure reason when `status === 'failed'`. */
  error: string | null;
  /** How many times the reaper has REQUEUED this job after a stale claim.
   *  Enqueued at 0; the reaper requeues once (attempts 0 → 1) then abandons on
   *  the next stall — a stuck build never loops forever. Absent on docs that
   *  predate the field (read as `?? 0`). */
  attempts: number;
  /** Capability fence for consumer-first rollouts. Legacy workers without the
   * exact advertised capability must not claim this row. */
  requiredWorkerCapability?: 'verified-core-v1' | 'verified-core-v2' | 'composition-private-v2' | 'presentation-republish' | null;
  /** Present only for accepted measured-first-playable revision 2 jobs. */
  measuredExecution?: MeasuredBuildExecutionV1 | null;
  /** Private lifecycle metadata for fresh public Composition V2 jobs. */
  compositionV2Lifecycle?: import('./compositionPublicCompletionV2.js').CompositionV2LifecycleV2;
}

export type BuildCommandDispatchState = 'queued' | 'claimed' | 'done' | 'failed';

/** Server-owned idempotency ledger projected from the deterministic
 * actionLog/build-command:* row written in the acceptance transaction. */
export interface BuildCommandRecord {
  creationId: string;
  commandId: string;
  callerUid: string;
  requestFingerprint: string;
  version: number;
  jobId: string;
  quotaIdentity: string;
  dispatchState: BuildCommandDispatchState;
  acceptedAt: number;
}

/** Lifecycle status of an async asset-studio generation job. */
export type AssetGenStatus = 'awaiting-core' | 'queued' | 'running' | 'succeeded' | 'failed';

/** Coarse progress stage a running job reports via heartbeats. The python
 *  asset CLI is a black box (no per-image progress), so the studio shows a
 *  stage + elapsed time, not a percentage. On the AUDIO path `postprocessing` is
 *  music's trim/crossfade/normalize/encode and `inspecting` is the decoded-byte
 *  gate that follows it — in that order, because what is inspected is the encoded
 *  artifact (plan rows 1.4 then 1.3). An SFX job never reports `postprocessing`:
 *  its candidate is the provider's WAV, inspected exactly as returned. */
export type AssetGenStage = 'generating' | 'postprocessing' | 'inspecting' | 'uploading';

/**
 * `assetGenJobs/{jobId}` — one async asset-studio generation. The kickoff
 * callable enqueues it (`queued`); a builder worker CLAIMS it (transactional
 * `queued → running` CAS on `status` + `workerId`), heartbeats a `stage` while
 * the python CLI runs, then marks it `succeeded` (+ `variantCount`) or
 * `failed`. MEMBER-readable so the studio shows live progress and recovers
 * variants after a reload/navigation.
 *
 * The four attribution fields (an {@link AttributionContext}) are captured at
 * kickoff and RIDE THE ROW so the builder-side completion bookkeeping (audit
 * `asset.generate` + `assets_created` badge) credits the attributed member
 * across the async boundary — a superadmin's attributed generation still earns
 * for the member, never the admin.
 */
export interface AssetGenJob {
  completionReconciledAt?: number | null;
  completionCheckedAt?: number | null;
  completionRecoveryEligible?: boolean;
  creationId: string;
  kind: CreationAssetKind;
  /** The creator-given asset name (see {@link CreationAsset.name}). */
  name: string;
  /** For visual kinds, the creator's image prompt. For `music`/`sfx` it is the
   *  RENDERED brief ({@link import('./audioGeneration.js').renderAudioBriefText}) —
   *  the exact text the provider will see, stored so the studio and the audit
   *  show what was asked rather than a second formatting of it. */
  prompt: string;
  /** Present EXACTLY on `music`/`sfx` jobs: the validated creator brief, which
   *  is the whole of what may reach an audio provider (ADR 0009 Q1). Null on
   *  every visual job. */
  audioBrief?: MusicBriefV1 | SfxBriefV1 | null;
  /** Platform-owned first-release work. It is accepted in the same transaction
   * as the version/build job and never consumes the interactive asset quota. */
  creationRun?: {
    runId: string;
    version: number;
    designRev: number;
    branch: 'visual' | 'music';
  } | null;
  /** Shared measured-build budget authority. Missing on interactive and
   * historical jobs, which retain their existing independent quota behavior. */
  measuredBudget?: MeasuredMediaBudgetAuthorityV1 | null;
  /** A succeeded composition media child's strip slots published without
   * art (see {@link CompositionDeferredMediaV1}); written atomically with its
   * terminal success. Absent/null on every other job. */
  deferredSlots?: CompositionDeferredMediaV1[] | null;
  /** A verified-core media child is durably accepted with its build but cannot
   * be claimed until the originating worker publishes the successful core
   * milestone. The release atomically binds the exact core media basis. */
  prerequisite?: {
    kind: 'verified-core';
    runInputDigest: string;
    mediaBasisDigest: string | null;
    releasedAt: number | null;
  } | {
    kind: 'composition-proof';
    authorityDigest: string;
    runInputDigest: string;
    mediaBasisDigest: string | null;
    releasedAt: number | null;
  } | null;
  /** Visual-run jobs derive provider prompts from the immutable version design
   * inside the trusted builder. No design or prompt is copied into this
   * member-readable job row. */
  plan?: {
    orientation: GameSettings['orientation'];
    assetNames?: string[];
    /** Server-validated prior visual job with the same immutable design.
     * Reuse its published candidates or retained staged milestones; never
     * repeat exact provider work during partial repair. */
    resumeFromJobId?: string;
  } | null;
  /** Cover-only source choice. Missing on historical jobs means game-assets,
   * preserving the platform default without rewriting old rows. */
  coverSource?: CoverGenerationSource | null;
  /** Platform gallery refreshes set this only after pinning the cover URL that
   * was visible at planning time. The worker replaces that exact cover with
   * its generated variant, but never overwrites a cover a creator changed
   * while the queued image generation was running. */
  autoAttachCover?: {
    expectedCoverUrl: string | null;
  } | null;
  /** Platform-managed title treatment. The worker generates one alpha PNG and
   * publishes it only while the creation title still equals expectedTitle. */
  titleOverlay?: {
    expectedTitle: string;
  } | null;
  status: AssetGenStatus;
  /** Coarse progress while `running`; null until the worker stamps one. */
  stage: AssetGenStage | null;
  /** Set when claimed; null while queued. */
  workerId: string | null;
  /** Last heartbeat (server millis); null while queued. */
  heartbeatAt: number | null;
  /** How many times the reaper has REQUEUED this job after a stale claim
   *  (enqueued at 0; requeued once then abandoned — mirrors {@link BuildJob}). */
  attempts: number;
  /** Attribution captured at kickoff (updatedBy semantics — the earning member). */
  createdBy: Uid;
  createdByHandle: string;
  /** Who actually performed the action (the caller — an admin for attributed gens). */
  actionPerformedBy: Uid;
  actionPerformedByHandle: string;
  /** Variants produced (set when `succeeded`; null otherwise). */
  variantCount: number | null;
  /** Failure reason when `status === 'failed'`. */
  error: string | null;
  /** Stable member-readable terminal classification. Absent on historical
   * rows; consumers fall back to status/error without guessing. */
  failureKind?:
    | 'provider'
    | 'timeout'
    | 'configuration'
    | 'cancelled'
    | 'worker'
    | 'validation'
    | null;
  /** `gs://` of this job's generation trace (capture v2, migration 0033) —
   *  the per-asset process record (prompt, phases, per-variant outcomes) in
   *  the PRIVATE logs bucket, stamped at the terminal transition. '' while
   *  running, when tracing was unavailable, or on jobs that predate it.
   *  Resolution is role-tiered via the serving callable, like build traces. */
  traceGcsUri: string;
  /** Present ONLY on platform-initiated art-completion follow-ups (enqueued
   *  when a build's asset go-live budget expired). `originVersion` is the
   *  build whose banked archetype this job regenerates; `variants` caps the
   *  generation (1 — completion doesn't multiply the spend the budget saved);
   *  `entityName` lets the completion build rebuild the full plan-asset shape
   *  for binding. Absent on every creator-initiated studio job. */
  completion?: {
    originVersion: number;
    variants: number;
    entityName?: string;
    /** Stable, reviewed production-repair identity. Lets a release-scoped
     *  repair resume failed names without regenerating successful siblings. */
    repairKey?: string;
  } | null;
  /** Present ONLY on `kind: 'strip'` jobs — the ONE strip-job payload shape
   *  (build enqueues per-entity with the full state list; the studio sends a
   *  length-1 `states`). Frame counts are ALWAYS derived from the vocabulary
   *  table (`ANIM_FRAME_DEFAULTS`), never stored here — the payload stays
   *  small because heartbeat UPDATEs replicate the full row. Null on every
   *  non-strip job. */
  strip?: {
    entityName: string;
    /** ANIM_STATE_NAMES members to generate, one strip image per state. */
    states: string[];
    /** The base-sprite variant to use as the generation reference. OMITTED on
     *  completion strip jobs whose entity's base sprite is ITSELF being
     *  completion-generated in the same wave — the strip worker resolves the
     *  reference at CLAIM time (succeeded sibling static's variant, else the
     *  currently attached base), so a strip job can never strand waiting on
     *  its base. */
    referenceVariantId?: string;
  } | null;
  createdAt: number;
  /** Set when the job reaches a terminal status. */
  finishedAt: number | null;
}

/**
 * `auditJobs/{jobId}` — one ON-DEMAND rendered playtest audit (the studio's
 * "Run visual audit" and the public-flip stamp check enqueue these; pipeline
 * builds audit inline and never file a row). Structurally an
 * {@link AssetGenJob} sibling: CAS-claimed, heartbeated, reaped, attribution
 * riding the row. The audit drives the version's PUBLISHED bundle; on success
 * the report is stamped here AND merged into the version's
 * {@link VersionGates.playtestAudit} via the one gates writer.
 */
export interface AuditJob {
  creationId: string;
  /** The live version whose published bundle the audit drives. */
  version: number;
  /** What the job runs against that bundle: the rendered playtest audit, or
   *  the splash-reel judgment/recording (the reel backfill, no model spend). */
  purpose: AuditJobPurpose;
  status: AssetGenStatus;
  workerId: string | null;
  heartbeatAt: number | null;
  attempts: number;
  createdBy: Uid;
  createdByHandle: string;
  actionPerformedBy: Uid;
  actionPerformedByHandle: string;
  /** The outcome (set when `succeeded`; null otherwise): the playtest report,
   *  or the reel outcome for a `splash-reel` job. */
  report: PlaytestAuditReport | import('./splashReel.js').SplashReelOutcomeV1 | null;
  error: string | null;
  createdAt: number;
  finishedAt: number | null;
}

export type AuditJobPurpose = 'playtest-audit' | 'splash-reel';

/**
 * `llmRequests/{id}` — one recorded call to an LLM provider (observability +
 * cost tracking). Written by the builder (trusted); read by superadmins only.
 * The full prompt is filed in Cloud Storage; `promptUrl` links to it and
 * `promptPreview` holds a head slice for at-a-glance display.
 */
export interface LlmRequestDoc {
  provider: string;
  model: string;
  /** The platform ACTIVITY that made the call — 'build' | 'iterate' | 'amend' |
   *  'review' | 'concept' | 'asset' | 'cover' | 'judge'. Stamped per call by the
   *  builder's log sink; the public stats view groups token spend by it
   *  (migration 0017 derived it for legacy rows from the build context). */
  task: string;
  /** Build context (empty/0 if the call was not a game build). */
  creationId: string;
  version: number;
  /** 1-based call index within the build. When the enrichment (design-brief)
   *  pass runs it is call 1 and the edit rounds follow; otherwise call 1 is
   *  the initial edit round, then repairs. */
  round: number;
  promptChars: number;
  promptPreview: string;
  /** `gs://` URI of the full prompt text in the PRIVATE logs bucket. The monitor
   *  resolves a short-lived signed URL on demand (R3-30); empty if filing failed.
   *  (Replaced the pre-R3-30 public `promptUrl`; old docs are backfilled by
   *  tools/ops/migrate-llm-prompt-logs.mjs.) */
  promptGcsUri: string;
  maxTokens: number | null;
  temperature: number | null;
  /** TOTAL prompt tokens, cache buckets included — Anthropic reports only the
   *  uncached remainder, so the adapter adds the buckets back before this is
   *  stored (migration 0035). */
  inputTokens: number;
  outputTokens: number;
  /** Prompt tokens written to the cache this call (billed at a premium). */
  cacheCreationTokens: number;
  /** Prompt tokens served FROM cache this call — a persistently zero value
   *  means the cached prefix is being invalidated and the write premium is
   *  pure loss. */
  cacheReadTokens: number;
  /** Estimated USD cost (from @djgg/llm pricing). */
  estimatedCostUsd: number;
  stopReason: string;
  /** The model's reply, captured like the prompt (capture v2, migration 0032):
   *  a head slice for display + the full text filed at
   *  `llm-logs/<id>/response.txt` in the PRIVATE logs bucket. All 0/''/'' on
   *  calls with no text reply (failed calls, image generation) and on rows
   *  that predate the capture. */
  responseChars: number;
  responsePreview: string;
  responseGcsUri: string;
  durationMs: number;
  ok: boolean;
  /** Error message when the call failed; '' otherwise. */
  error: string;
  createdAt: number;
}

/**
 * Bump whenever the Editors' Pick RUBRIC changes what a score MEANS — a
 * criterion added to the judgement, a weighting reversed, an anchor redefined.
 * Not for wording that leaves verdicts identical.
 *
 * Verdicts are cached per (creation, version) so an unchanged build is never
 * re-scored. Without this in the key, a rubric change can never reach a game
 * whose BUILD has not moved — which is not hypothetical: on 2026-08-23 the
 * rubric was rewritten to stop rewarding paperwork precisely because the
 * hand-written natives scored 22-42, and every native sits at version 0, so all
 * six were served from cache and the fix reached none of them.
 *
 * Same principle as `AUDIT_SCHEMA_VERSION`: two scores from different rubrics
 * are two different readings and must never be pooled.
 *
 *  1 — dossier-only (title, description, design doc, engagement counters).
 *  2 — playtest-aware: the live build's audit outranks the dossier, and effort
 *      (iteration count, asset count, depth of writing) is explicitly not
 *      quality.
 */
export const REVIEW_RUBRIC_VERSION = 2;

/**
 * `editorsPickReviews/{creationId}:{version}` — one LLM review verdict for a
 * specific creation VERSION, written by the pickEditorsPicks function after the
 * builder scores it. The (creationId, version) composite key is what lets the
 * reviewer SKIP re-reviewing an unchanged version: a run only sends
 * un-recorded pairs to the LLM and reuses these scores for ranking. The
 * `rationale` is an internal editorial note — superadmin-read only; the trusted
 * function is the sole writer (append-only, like {@link LlmRequestDoc}).
 */
export interface EditorsPickReviewDoc {
  creationId: string;
  /** The creation's `activeVersion` when it was reviewed. */
  version: number;
  /** The rubric this verdict was produced under ({@link REVIEW_RUBRIC_VERSION}).
   *  Part of the skip-re-review key: a verdict from an older rubric is a
   *  different reading, not a reusable one. Absent on rows written before
   *  rubric versioning — treated as rubric 1. */
  rubricVersion?: number;
  /** Absolute quality score 0–100 (rubric-anchored so it's comparable across
   *  games reviewed in different batches). */
  score: number;
  /** Short verdict tag (e.g. `'strong' | 'solid' | 'weak'`). */
  verdict: string;
  /** The reviewer's short editorial rationale. */
  rationale: string;
  /** Highlight tags the reviewer surfaced (e.g. `['polished','original']`). */
  tags: string[];
  /** `provider/model` that produced the verdict. */
  model: string;
  reviewedAt: number;
}

/**
 * `portalSessions/{sessionId}` — one row per portal SPA session, recorded
 * through the trackVisit callable (session id minted client-side, kept in
 * sessionStorage). `views` counts route navigations within the session. No
 * client read/write access — the public stats view exposes only the totals.
 */
export interface PortalSession {
  views: number;
  firstSeen: number;
  lastSeen: number;
}

// ── Admin Stats (adminStats/{windowStart}) ──────────────────────────────────
//
// One snapshot per 4h window written by the `scheduledAdminStats` cron and read
// through the `getAdminStats` callable. `QuantSnapshot` is the SINGLE source of
// the `quant` blob's shape — bumped alongside `schemaVersion` when tiles change,
// so historical rows stay renderable. Every source sub-object is optional/null:
// a source that failed for a window is absent from `quant` and stamped in
// `provenance`, and a later retry of the same window fills it in.

/** Per-source collection outcome, stamped for every window (fail-loud). */
export interface AdminStatsSourceStatus {
  status: 'ok' | 'skipped' | 'error';
  /** Short reason when not ok (e.g. 'timeout@30s', 'GITHUB_STATS_TOKEN unset'). */
  note?: string;
}

/** Provenance for one window — which sources landed, and first-window flag. */
export interface AdminStatsProvenance {
  postgres?: AdminStatsSourceStatus;
  github?: AdminStatsSourceStatus;
  gcp?: AdminStatsSourceStatus;
  llm?: AdminStatsSourceStatus;
  /** True on the very first snapshot (no prior window to diff against). */
  firstWindow?: boolean;
}

/** Platform-wide totals shared by the public Stats view and Admin Stats. */
export interface PlatformTotals {
  uniqueSessions: number;
  pageViews: number;
  totalPlays: number;
  gameViews: number;
  multiplayerMatches: number;
  gamesCreated: number;
  nativeGames: number;
  completedForks: number;
  gameIterations: number;
  assetsAttached: number;
  assetVariantsGenerated: number;
  totalUsers: number;
  totalLikes: number;
  totalComments: number;
}

/** Exact timestamp-backed product activity inside one 4h snapshot window. */
export interface AdminPlatformWindow {
  gamesCreated: number;
  nativeGamesAdded: number;
  v1GamesLive: number;
  completedForks: number;
  gameIterations: number;
  assetVariantsGenerated: number;
  multiplayerMatches: number;
  newUsers: number;
  newSessions: number;
  likes: number;
  comments: number;
}

export type AdminLlmCostLane = 'game-v1' | 'iteration' | 'assets' | 'concept' | 'other';

// ── Named activity detail (Activity Summary) ────────────────────────────────
//
// `AdminPlatformWindow` counts THINGS; these name them, so the Activity Summary
// can say "Rotor Rescue took 12 plays" instead of "12 plays". Titles and handles
// are user-authored → treated as untrusted text everywhere downstream (capped
// and control-char stripped by the collector, rendered as plain text, and passed
// to the LLM inside untrusted-data tags).

/**
 * CUMULATIVE per-game engagement counters sampled at the window boundary — the
 * same monotone counters the public Stats page totals. There is no per-play
 * event ledger in djgg (`creations.plays_count` is a bare counter), so per-game
 * plays for a RANGE come from DIFFING this sample against an earlier window's,
 * exactly as the dashboard's own "Total plays" tile does platform-wide.
 */
export interface AdminGameEngagementRow {
  creationId: string;
  slug: string;
  title: string;
  kind: 'creation' | 'native';
  creatorHandle: string;
  plays: number;
  views: number;
  likes: number;
  comments: number;
}

/** action_log rows in THIS window, named by game (`{slug} × {action}`). */
export interface AdminGameActionRow {
  slug: string;
  title: string;
  action: string;
  count: number;
}

/** Who was busy in THIS window, by action_log actor handle. */
export interface AdminActorRow {
  handle: string;
  actions: number;
}

/** Creations born in THIS window (from `creations`, not the action log). */
export interface AdminNewGameRow {
  slug: string;
  title: string;
  kind: 'creation' | 'native';
  creatorHandle: string;
  createdAt: number;
}

/**
 * The named counterpart to {@link AdminPlatformWindow}. Absent on snapshots
 * collected before the Activity Summary shipped — consumers MUST treat absence
 * as "not recorded", never as "nothing happened".
 */
export interface AdminActivityDetail {
  /** Cumulative counters (see {@link AdminGameEngagementRow}) — diff to window. */
  gameEngagement: AdminGameEngagementRow[];
  /** True when more games exist than the collector's sample cap. */
  gameEngagementCapped: boolean;
  actionsByGame: AdminGameActionRow[];
  actionsByActor: AdminActorRow[];
  newGames: AdminNewGameRow[];
}

// ── Creation Overview insights (getCreationInsights callable) ────────────────
//
// The studio Overview tab's Phase-2 data: WHO played a game (+ when) and the
// FULL per-activity LLM spend on it. Both aggregate server-only source tables
// (`user_game_finishes`, `llm_requests`) that have no client read surface, so
// they are exposed ONLY through the member-gated getCreationInsights callable —
// never a direct read. Reads are restricted to the creation's members + admin
// (the whole hub is member-gated); the players list is identity-bearing by the
// owner's decision (2026-07-22), visible only to that game's own members.

/**
 * One player who FINISHED at least one run of a creation — projected from
 * `user_game_finishes` (⨝ `creator_profiles` for display), summed across all
 * days. Solo finishes come from the recordGameFinish funnel; multi from the
 * server-authoritative MatchResultWriter.
 */
export interface InsightPlayer {
  uid: Uid;
  /** Denormalized display handle ('' if the player has no profile row). */
  handle: string;
  /** PROFILE_AVATARS catalog id ('' if unchosen). */
  avatarKey: string;
  /** Which modes this player finished in. */
  mode: 'solo' | 'multi' | 'both';
  /** Total finishes (same-day repeats summed). */
  runs: number;
  firstAt: number;
  lastAt: number;
}

/** Per-activity LLM spend on one creation (from `llm_requests`, lane-grouped —
 *  the same CASE derivation as the admin cost breakdown). */
export interface InsightCostRow {
  activity: AdminLlmCostLane;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

/** The Overview tab's members-only insights payload. */
export interface CreationInsights {
  /** Identified players who finished a run, most-recent first. */
  players: InsightPlayer[];
  /** Full LLM cost (ALL activities — build/iterate + asset-gen + concept),
   *  which supersedes the build-only estimate the portal computes client-side. */
  cost: {
    byActivity: InsightCostRow[];
    totalInputTokens: number;
    totalOutputTokens: number;
    totalCostUsd: number;
  };
}

/**
 * The canonical window-level LLM breakdown. The UI folds these rows by lane,
 * provider, model, or task; collectors must not persist parallel rollups.
 */
export interface AdminLlmCostRow {
  lane: AdminLlmCostLane;
  task: string;
  provider: string;
  model: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

/**
 * ALL-TIME LLM spend on one game, lane-split — the admin "cost per game"
 * table. A point-in-time sample taken at each snapshot (like `topTables`),
 * NOT a window aggregate: rows must never be summed across snapshots.
 */
export interface AdminGameCostRow {
  creationId: string;
  /** Denormalized at sample time; '(deleted)' when the creation row is gone. */
  title: string;
  /** '' when the creation row is gone (no page to link). */
  slug: string;
  /** The base v1 build (lane 'game-v1'). */
  v1CostUsd: number;
  /** All post-v1 builds (lane 'iteration'). */
  iterationCostUsd: number;
  assetsCostUsd: number;
  conceptCostUsd: number;
  otherCostUsd: number;
  totalCostUsd: number;
  calls: number;
}

/** Cumulative pg_stat_statements counters sampled at the window boundary. */
export interface AdminDbQueryRow {
  queryId: string;
  /** Normalized, literal-redacted SQL suitable for the admin dashboard. */
  query?: string;
  calls: number;
  meanExecMs: number;
  maxExecMs: number;
  totalExecMs: number;
  rows: number;
}

export interface AdminDbQueryStats {
  available: boolean;
  note?: string;
  totalCalls: number;
  totalRows: number;
  totalExecMs: number;
  slowCalls: number;
  slowStatements: number;
  maxExecMs: number | null;
  statsResetAt: number | null;
  topSlow: AdminDbQueryRow[];
  /** Highest cumulative execution-time statements in the latest sample. */
  topQueries?: AdminDbQueryRow[];
}

/** Supabase/Postgres stats — all sourced by the raw-SQL collector. */
/**
 * One drill-down row of the first-draft keep/discard signal (plan Unit 1b).
 * STATED INTENT, not retention: a member said "keep iterating" or "discard"
 * about the creation's FIRST live version. Reported next to the population
 * (live first drafts, failed first builds) and coverage (rated) so an unrated
 * or never-built game is never mistaken for a kept one. `cohort` splits the
 * bot squad / operator accounts (`PRESENCE_EXCLUDED_HANDLES`) from people.
 * A creation's verdict is its creator's response, else its editors' majority;
 * admin evaluations are counted separately.
 */
export interface AdminFirstDraftFeedbackRow {
  family: string;
  pipelineId: string;
  /** `provider/model` the first build actually ran on. */
  engine: string;
  cohort: 'people' | 'automation';
  firstDraftsLive: number;
  firstDraftsFailed: number;
  rated: number;
  keep: number;
  discard: number;
  adminKeep: number;
  adminDiscard: number;
}

export interface AdminPostgresStats {
  dbSizeBytes: number;
  backends: number;
  xactCommit: number;
  xactRollback: number;
  blksHit: number;
  blksRead: number;
  tupReturned: number;
  deadlocks: number;
  topTables: Array<{ table: string; totalBytes: number }>;
  buildJobs: Array<{ status: string; count: number }>;
  /** action_log rows in THIS window, grouped by action. */
  actionLogWindow: Array<{ action: string; count: number }>;
  /** llm_requests in THIS window at the one canonical drill-down grain. */
  llmCosts: AdminLlmCostRow[];
  /** All-time cost per game (top-N by total), sampled at this snapshot.
   *  Optional: absent on pre-feature historical rows. */
  topGameCosts?: AdminGameCostRow[];
  /** Same totals as the public Stats view, sampled at this window boundary. */
  platformTotals?: PlatformTotals;
  /** First-draft keep/discard verdicts (0077) at the drill-down grain, all-time,
   *  sampled at this snapshot. Absent on pre-feature historical rows. */
  firstDraftFeedback?: AdminFirstDraftFeedbackRow[];
  /** Exact timestamp-backed activity inside THIS window. */
  platformWindow?: AdminPlatformWindow;
  /** Named activity detail (schema v4+). Absent = not recorded, NOT inactivity. */
  activityDetail?: AdminActivityDetail;
  /** pg_stat_statements sample; optional on pre-schema-v2 historical rows. */
  dbQueries?: AdminDbQueryStats;
  /** RLS policy inventory (public schema). No per-policy hit counts exist. */
  rlsPolicyCount: number;
  /** Static cron inventory (code-known). */
  crons: Array<{ name: string; schedule: string }>;
}

/** GitHub repo activity for the window (rebase-merge repo: PRs ≠ commits). */
export interface AdminGithubStats {
  prsOpen: number;
  prsMergedWindow: number;
  prsClosedWindow: number;
  mergedPerDay: number;
  timeToMergeHoursP50: number | null;
  timeToMergeHoursP90: number | null;
  commitsWindow: number;
  contributors: Array<{ login: string; commits: number }>;
  /** Merges into `main` in the window — the deploy-candidate proxy. */
  deployCandidateMerges: number;
  /** Bounded list (title/author scrubbed + capped) for the digest's dev bucket. */
  mergedPrs: Array<{
    number: number;
    title: string;
    author: string;
    mergedAt: number;
    hoursToMerge: number;
  }>;
  /** Current open PRs, captured at the snapshot boundary. */
  openPrs?: Array<{
    number: number;
    title: string;
    summary: string;
    author: string;
    createdAt: number;
    base: string;
    draft: boolean;
  }>;
}

/** GCP Monitoring/Logging/Run stats for the window. */
export interface AdminGcpStats {
  services: Array<{
    service: string;
    requestCount: number;
    errorCount: number;
    latencyMsP50: number | null;
    latencyMsP95: number | null;
    latencyMsP99: number | null;
    cpuUtilP95: number | null;
    memUtilP95: number | null;
  }>;
  storageBytes: Array<{ bucket: string; bytes: number }>;
  taskQueueDepth: number | null;
  logCounts: Array<{ service: string; severity: string; count: number }>;
  /** Latest redacted log samples in this window (bounded by the collector). */
  recentLogs?: Array<{
    insertId: string;
    timestamp: number;
    service: string;
    severity: string;
    message: string;
  }>;
  deploys: Array<{ service: string; revision: string; createTime: number }>;
}

/** The `quant` blob — one optional sub-object per source. */
export interface QuantSnapshot {
  postgres?: AdminPostgresStats | null;
  github?: AdminGithubStats | null;
  gcp?: AdminGcpStats | null;
}

/** `adminStats/{windowStart}` — one operational-stats snapshot per 4h window. */
export interface AdminStatsSnapshot {
  /** ms epoch, floored to the 4h grid — the doc id and PK. */
  windowStart: number;
  windowEnd: number;
  schemaVersion: number;
  quant: QuantSnapshot;
  /** LLM digest — the full grouped analysis; null until/unless the summarizer
   *  succeeds for the window. */
  narrative: string | null;
  /** LLM digest — a few short headline bullets for the news-ticker; null when
   *  the narrative is (same fail-loud contract). */
  narrativeBullets: string[] | null;
  provenance: AdminStatsProvenance;
  createdAt: number;
}

// ── Activity Summary (activitySummaries/{rangeKey}) ─────────────────────────
//
// "Deej's Gazette": one agent-written summary of platform activity per DASHBOARD
// RANGE, cached 24h. The row is simultaneously the cache AND the lock — `status`
// + `lockedAt` are claimed by a compare-and-swap so exactly one generator runs
// per range across all function instances (see ActivitySummaryRepo.claim).

/** Cache key: one cached summary per selectable dashboard range. */
export type ActivityRangeKey = '4h' | '24h' | '3d' | '7d' | '14d' | '30d' | 'all';

export type ActivitySummaryStatus = 'running' | 'ready' | 'error';

/** One story beat — a claim that MUST be backed by `metric`. */
export interface ActivityBeat {
  emoji: string;
  title: string;
  line: string;
  metric: string;
}

/** A playful superlative ("Most played") pinned to a real number. */
export interface ActivityAward {
  title: string;
  winner: string;
  metric: string;
}

/**
 * One thing that shipped in the range, with what it means for the platform.
 *
 * `pr` is the merge that carried it. This repo deploys from main by hand and
 * only the builder/game-server leave Cloud Run revisions behind (Hosting leaves
 * none), so a merge into main is the honest evidence — the copy says "merged",
 * it does not assert a deploy nobody recorded.
 */
export interface ActivityShipped {
  /** Short human name for the change — never the raw commit subject. */
  title: string;
  /** One line on what it improves for the platform or its people. */
  meaning: string;
  pr: number | null;
}

/**
 * One merge behind "and 4 more" — RAW facts, never agent prose.
 *
 * The agent only writes up to three celebrated items; the rest are listed
 * verbatim from the merge itself when the reader expands the count, so nothing
 * here is a claim the model could get wrong.
 */
export interface ActivityShippedMerge {
  pr: number;
  /** The merged PR title as it stands on GitHub (conventional-commit subject). */
  title: string;
  /** `feat` | `fix` | `perf`, parsed from that subject. */
  kind: string;
}

/** The rendered payload. Every field is plain text — never markdown or HTML. */
export interface ActivityGazette {
  headline: string;
  subhead: string;
  beats: ActivityBeat[];
  awards: ActivityAward[];
  /** What the team shipped in the range, and why it matters. May be empty. */
  shipped?: ActivityShipped[];
  /** Shipped items beyond the rendered few, so "and 4 more" stays honest. */
  shippedMore?: number;
  /**
   * The merges that "and 4 more" stands for — what the reader sees when they
   * expand the count. Ordered as the rollup ranks them (features first, then
   * newest) and capped by it, so this can be SHORTER than {@link shippedMore}
   * on a long range; the panel says so when it is.
   */
  shippedRest?: ActivityShippedMerge[];
  closer: string;
}

/** `activitySummaries/{rangeKey}` — the cache row, lease row, and result. */
export interface ActivitySummary {
  rangeKey: ActivityRangeKey;
  status: ActivitySummaryStatus;
  /** Lease stamp of the in-flight generator; older than the lease is reclaimable. */
  lockedAt: number | null;
  generatedAt: number | null;
  /** `generatedAt + 24h` — a `ready` row past this regenerates on next landing. */
  expiresAt: number | null;
  /** The snapshot span this summary actually describes (the "as of" stamp). */
  coveredFrom: number | null;
  coveredTo: number | null;
  windowCount: number;
  /** How many covered windows carried named detail (see AdminActivityDetail). */
  detailWindows: number;
  schemaVersion: number;
  payload: ActivityGazette | null;
  /** Fail-loud: set with status 'error'; never a fabricated payload. */
  errorNote: string | null;
  model: string | null;
  costUsd: number | null;
  createdAt: number;
  updatedAt: number;
}

/**
 * `creatorProfiles/{uid}` — a creator's public profile card. Doubles as the
 * platform's USER DIRECTORY (public-read): the collaborator invite picker
 * lists + searches these docs, and the portal upserts a stub on first
 * sign-in so every real account is discoverable.
 */
export interface CreatorProfile {
  handle: string;
  bio: string;
  /** PROFILE_AVATARS catalog id (empty if not chosen). */
  avatarKey: string;
  joinedAt: number;
  /** Stamped ONCE by the updateMyProfile callable the first time the profile
   *  carries both a valid handle and a chosen avatar (the /welcome claim
   *  step). Absent/null = first-sign-in stub not yet claimed. Server-owned. */
  claimedAt?: number | null;
  /** Count of creations owned (maintained by a function; typed default 0). */
  gamesCount: number;
  /** Mirror of the auth custom claim, maintained by setUserRole (claims are
   *  the enforcement source; this is for display). Absent/null = plain user. */
  role?: 'creator' | 'superadmin' | null;
  /** Superadmin-set per-creator quota overrides (absent fields fall back to
   *  the global {@link PlatformSettings} caps). Server-only; set via
   *  `setQuotaOverride`. */
  quotaOverrides?: QuotaOverrides;
}

/**
 * `users/{uid}/badges/{badgeId}` — one EARNED achievement badge (badge ids are
 * the generated {@link import('./badgeDefs.js').BADGE_DEFS} catalog). Written
 * ONLY by the server-side badge engine (`badges/evaluate.ts` — awarded once,
 * `awardedAt` never overwritten). Public-read + realtime-published (0023): the
 * portal's celebration overlay fires on the INSERT and the /u page shows the
 * wall to anyone.
 */
export interface UserBadge {
  badgeId: string;
  awardedAt: number;
}

/**
 * `users/{uid}/gameFinishes/{gameKey}:{mode}:{day}` — the per-day game-finish
 * rollup behind the play-metric badges (locked decision 5: a play day = a day
 * you FINISHED at least one run). One doc per (gameKey, mode, UTC day);
 * repeats on the same day bump `runs`. `mode: 'solo'` rows come from the
 * recordGameFinish callable; `'multi'` ONLY from the server-authoritative
 * MatchResultWriter. Server-only — no client read/write surface (progress
 * reads go through the `my_badge_progress` view).
 */
export interface UserGameFinish {
  /** Creation id or native slug the finish belongs to. */
  gameKey: string;
  mode: 'solo' | 'multi';
  /** UTC finish day, `yyyy-mm-dd` (utcDayKey). */
  day: string;
  /** Finish count for this (gameKey, mode) on this day. */
  runs: number;
  firstAt: number;
  lastAt: number;
}

/**
 * `users/{uid}/gamePlays/{gameKey}` — one row per (player, game): when they
 * first and last PLAYED it. Written from every presence beat that names a game
 * (0047), so unlike {@link UserGameFinish} it counts a run that never reached
 * game over, and unlike `userPresence` (one row per uid, "right now") it is
 * HISTORY. Backs the game page's "Recently played by" rail through the
 * `public_game_recent_players` view.
 *
 * Server-only — no client read/write surface. The accounts that are not people
 * (unclaimed profiles, the operator/bot squad) are dropped by the writer, so
 * every row here is a real player.
 */
export interface GamePlay {
  /** Creation id or native slug the play belongs to. */
  gameKey: string;
  firstAt: number;
  lastAt: number;
  /** Presence beats seen for this pair — engagement, not session count. */
  beats: number;
}

/** Durable lifetime fact that a human user has shared the best underlying
 * score/rating on one leaderboard. Server-written during recompute. */
export interface UserLeaderboardFirst {
  gameId: string;
  firstAchievedAt: number;
  maxHumanParticipants: number;
}

/** Durable fact that two users both participated in the same active Clubhouse
 * room. Stored in both directions so each user's distinct-peer total is cheap. */
export interface UserChatPeer {
  peerUid: Uid;
  firstChattedAt: number;
}

/**
 * Per-creator overrides of the global daily-quota caps
 * (`PlatformSettings.maxVersionsPerDay` etc). A number replaces the global cap
 * for that creator; `null` lifts the cap entirely (unlimited); an absent
 * field falls back to the global setting.
 */
export interface QuotaOverrides {
  maxVersionsPerDay?: number | null;
  maxAssetGensPerDay?: number | null;
  maxConceptAnalysesPerDay?: number | null;
  maxAuditsPerDay?: number | null;
}

/** Everything the platform audits. Dot-namespaced `<subject>.<verb>`. */
export type ActionLogAction =
  | 'media.sfxBind'
  | 'pipeline.publish'
  | 'pipeline.visibility'
  | 'creation.create'
  | 'creation.fork'
  | 'creation.update'
  | 'creation.publish'
  | 'creation.unpublish'
  | 'creation.delete'
  | 'settings.update'
  | 'platform.settings.update'
  | 'build.request'
  /** A composed revision run started (its build is a later `build.request`). */
  | 'build.revisionStart'
  /** An operator restored a replaced composed revision run. */
  | 'build.revisionRestore'
  | 'build.artCompletion'
  | 'build.cancel'
  | 'build.requeue'
  | 'workshop.save' | 'workshop.play' | 'creation-show.event'
  | 'build.abandon'
  | 'build.superseded'
  | 'build.heal'
  | 'version.setActive'
  | 'version.delete'
  | 'editor.add'
  | 'editor.remove'
  | 'asset.generate'
  | 'asset.cancel'
  | 'audit.run'
  | 'asset.upload'
  | 'asset.attach'
  | 'asset.detach'
  | 'concept.analyze'
  | 'design.manualDraftSave'
  | 'design.manualDraftPromote'
  | 'design.update'
  | 'design.amend'
  | 'design.artplan'
  | 'comment.post'
  | 'comment.delete'
  | 'feedback.record'
  | 'profile.update'
  | 'role.set'
  | 'quota.override'
  | 'editorsPick.run'
  | 'invite.create'
  | 'invite.accept'
  | 'invite.cancel';

/**
 * `actionLog/{id}` — the platform AUDIT LOG: one append-only entry per
 * mutating action, written by the trusted functions after the mutation
 * succeeds (admin SDK; superadmin-read). Spec fields `created_at/updated_by/
 * action_performed_by` map to camelCase per repo convention.
 *
 * The attribution invariant: `updatedBy` is the user the change is CREDITED
 * to — the acting member themselves, or, when a superadmin edits a game they
 * are not a member of, the collaborator they explicitly attributed the change
 * to (never the admin). `actionPerformedBy` is ALWAYS the real signed-in uid,
 * so admin interventions remain fully visible.
 */
export interface ActionLogEntry {
  action: ActionLogAction;
  /** Subject creation (absent for non-creation actions like role.set). */
  creationId?: string;
  /** Display snapshots so log rows survive creation deletion/renames. */
  slug?: string;
  title?: string;
  /** Attributed user (credited author of the change). */
  updatedBy: Uid;
  updatedByHandle: string;
  /** Real signed-in uid that performed the action. */
  actionPerformedBy: Uid;
  actionPerformedByHandle: string;
  /** Small action-specific extras (e.g. { version: 3 }, { targetUid: '…' }). */
  details?: Record<string, string | number>;
  createdAt: number;
}

/**
 * Superadmin curation inputs + run metadata for the Editors' Pick reviewer
 * (rides on `platformSettings/global`). `pinned` slugs are ALWAYS in the
 * active list; `excluded` slugs are NEVER picked; the weekly LLM run fills the
 * remaining slots up to the target count and honors both (the override survives
 * the next cron). The RESOLVED pick list is NOT stored here — it lives on each
 * creation's {@link CreationEditorsPick}, and the gallery row derives from
 * those.
 */
export interface EditorsPickConfig {
  /** Slugs force-INCLUDED in every run (admin override). */
  pinned: string[];
  /** Slugs force-EXCLUDED from every run (admin blocklist). */
  excluded: string[];
  /** Millis the reviewer last ran (undefined until the first run). */
  lastRunAt?: number;
  /** Edition label of the last run (e.g. `'2026-W29'`). */
  edition?: string;
}

/** Owner-controlled creator feature switches. These are product choices that
 * take effect from Admin without rebuilding or restarting a service. */
export interface CreationFeatureControls {
  creationFilms: boolean;
  workshopSelections: boolean;
  mediaReviewPilot: boolean;
  videoReferences: boolean;
  suggestionPass: boolean;
  diagnosisLoop: boolean;
  qualityLoop: boolean;
  assetGoLiveBudget: boolean;
}

/** Server/private owner settings. Unlike PlatformSettings, this document is
 * never public because it can name private or unlisted creations. */
export interface PlatformAdminSettings {
  /** Must match the paired public settings revision. */
  revision: number;
  mediaReviewPilotCreationIds: string[];
  /** Retired pilot membership retained for historical reads; never selects new builds. */
  measuredFirstPlayablePilotUids: string[];
}

/**
 * `platformSettings/global` — the single superadmin-editable settings doc.
 */
export interface PlatformSettings {
  /** Monotonic compare-and-swap revision for the Admin settings form. */
  revision: number;
  /** Normalized to the universal Deej-led journey. Classic remains in the type
   * for historical documents and stale client compatibility. */
  creationExperience: 'classic' | 'deej-led';
  /** Creator-facing product controls managed alongside the journey rollout. */
  creationFeatures: CreationFeatureControls;
  /** Default engine for new creations. */
  defaultEngine: EngineRef;
  /** Max concurrent builder claims. */
  buildConcurrency: number;
  /** Per-creator daily version cap. */
  maxVersionsPerDay: number;
  /** Per-creator daily asset-generation cap (each generate = Gemini image
   *  calls; billed like builds, capped like builds). */
  maxAssetGensPerDay: number;
  /** Per-creator daily concept-analysis cap (each analysis = up to 3 page
   *  fetches + ~4 LLM calls; billed like builds, capped like builds). */
  maxConceptAnalysesPerDay: number;
  /** Per-creator daily on-demand playtest-audit cap (each audit = a browser
   *  drive + one VLM screenshot judgment on the builder — cheap next to a
   *  build, but not free). */
  maxAuditsPerDay: number;
  /** Slugs featured on the gallery landing. */
  galleryFeaturedSlugs: string[];
  /** Editors' Pick curation config — admin pins/excludes + reviewer run
   *  metadata. Always populated by {@link PlatformSettingsRepo} (defaults to
   *  empty pins/excludes). See {@link EditorsPickConfig}. */
  editorsPick: EditorsPickConfig;
  /** Message-of-the-day banner (empty = hidden). */
  motd: string;
  /** Autonomous quality loop: the MAX number of build passes the builder runs
   *  in-process for one version (best-of-N keep-best). 1 disables the loop. */
  maxQualityPasses: number;
  /** Autonomous quality loop: the critic score (0..1) at/above which a build is
   *  "good enough" and the loop stops early. Below it, the loop spends another
   *  pass (up to {@link maxQualityPasses}). */
  qualityBar: number;
  /** Whether a creator may ask a PROVIDER to generate music / SFX. These
   * owner-controlled Admin settings are the sole spend authorization. Provider
   * credentials still have to be healthy, but no deployment flag is required. */
  audioGeneration: AudioGenerationFlags;
  /** Asset go-live budget: after the code gates pass, the build waits at most
   *  this long (ms) for the build-time asset library before banking the
   *  still-missing archetypes and publishing; the missed ones are regenerated
   *  asynchronously and bound by an art-completion build. */
  assetGoLiveBudgetMs: number;
  /** First playable releases wait longer for approved media so the rendered
   * audit evaluates the intended art and soundtrack. Later iterations use
   * {@link assetGoLiveBudgetMs}. */
  firstReleaseMediaWaitMs: number;
}
